import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Cable,
  Check,
  ChevronDown,
  Columns3,
  Download,
  Gauge,
  Search,
  TreePine,
  Zap,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import AppBar from 'components/AppBar';
import './DeliveryTracking.css';
import { getAllPurchaseOrders, parsePoItemsField } from '../services/purchaseOrdersService';
import { fetchCableDeliveriesStateMap, upsertCableDelivery } from '../services/cableDeliveriesService';
import {
  fetchConcretePolesDeliveriesStateMap,
  upsertConcretePolesDelivery
} from '../services/concretePolesDeliveriesService';
import { fetchPolesEditsMap, upsertPolesEdit } from '../services/polesDeliveriesService';
import {
  backupContractScopeToLocalStorage,
  fetchDeliveryContractScope,
  saveDeliveryContractScopeState
} from '../services/deliveryContractQtyService';
import { correctPoItemName, is100sqmmAcsrItemName } from '../utils/poItemNameNormalize';
import PolesPoQuantitiesTable, {
  isWoodenPolesPurchaseOrder
} from '../components/PolesPoQuantitiesTable';
import { isPolesLot10OnlyRole, isLot10ScopedSession, isReadOnlySession } from '../services/authService';
import { useAuthSession } from '../contexts/AuthSessionContext';

const CABLE_TABLES_STORAGE_KEY = 'deliveryTracking:cableTablesState:v1';
const CONCRETE_TABLES_STORAGE_KEY = 'deliveryTracking:concretePolesTablesState:v1';
const CABLE_DELIVERY_COLUMN_COUNT = 8;
const CABLE_DELIVERY_COL_START = 3;
const MATERIAL_DELIVERY_TABS = ['cables', 'concrete-poles', 'transformers', 'meters'];

const MATERIAL_LOT_TABS = ['LOT 8', 'LOT 10'];

const poMatchesMaterialLotTab = (po, lotTab) => {
  const lot = String(po?.lot || '');
  if (/\blot[\s\-_]*8\b/i.test(String(lotTab || ''))) return /\blot[\s\-_]*8\b/i.test(lot);
  if (/\blot[\s\-_]*10\b/i.test(String(lotTab || ''))) return /\blot[\s\-_]*10\b/i.test(lot);
  return lot.trim() === String(lotTab || '').trim();
};

const parsePoLineQty = (raw) => {
  if (raw === null || raw === undefined || raw === '') return 0;
  const num = Number(String(raw).replace(/,/g, '').trim());
  return Number.isFinite(num) ? num : 0;
};

const CABLE_QTY_UNIT_OPTIONS = ['KM', 'M', 'PC'];

function MaterialPoQuantitiesTable({
  title,
  lotTab,
  purchaseOrders = [],
  aggregateByCableType = false,
  cableTablesState = {},
  computeRowDelivered,
  normalizePoItemsForTable,
  getPoQtyNumeric,
  contractStorageScope = '',
  readOnly = false
}) {
  const [contractQtys, setContractQtys] = useState({});
  const [cableUnits, setCableUnits] = useState({});
  const [contractScopeLoading, setContractScopeLoading] = useState(true);
  const [contractSaveState, setContractSaveState] = useState('idle');
  const [contractSaveError, setContractSaveError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setContractScopeLoading(true);
    (async () => {
      const state = await fetchDeliveryContractScope(contractStorageScope);
      if (cancelled) return;
      setContractQtys(state.contractQtys || {});
      setCableUnits(state.cableUnits || {});
      setContractScopeLoading(false);
      setContractSaveState('idle');
      setContractSaveError('');
    })();
    return () => {
      cancelled = true;
    };
  }, [contractStorageScope, lotTab]);

  const handleCableUnitChange = (itemKey, rawValue) => {
    if (readOnly || contractScopeLoading) return;
    const value = CABLE_QTY_UNIT_OPTIONS.includes(rawValue) ? rawValue : '';
    setCableUnits((prev) => {
      const next = { ...prev, [itemKey]: value };
      setContractSaveState('idle');
      setContractQtys((currentContract) => {
        backupContractScopeToLocalStorage(contractStorageScope, currentContract, next);
        return currentContract;
      });
      return next;
    });
  };

  const handleContractQtyChange = (itemKey, rawValue) => {
    if (readOnly || contractScopeLoading) return;
    const value = String(rawValue ?? '');
    setContractQtys((prev) => {
      const next = { ...prev, [itemKey]: value };
      setContractSaveState('idle');
      setCableUnits((currentUnits) => {
        backupContractScopeToLocalStorage(contractStorageScope, next, currentUnits);
        return currentUnits;
      });
      return next;
    });
  };

  const handleSaveContractScope = async () => {
    if (readOnly || contractScopeLoading) return;
    setContractSaveState('saving');
    setContractSaveError('');
    try {
      await saveDeliveryContractScopeState(
        contractStorageScope,
        contractQtys,
        cableUnits
      );
      setContractSaveState('saved');
      window.setTimeout(() => setContractSaveState('idle'), 2500);
    } catch (err) {
      console.error('saveDeliveryContractScopeState failed:', err);
      setContractSaveState('error');
      setContractSaveError(
        err?.message || 'Could not save to Appwrite. Check your connection and try again.'
      );
    }
  };

  const contractSaveButtonLabel =
    contractSaveState === 'saving'
      ? 'Saving…'
      : contractSaveState === 'saved'
        ? 'Saved'
        : 'Save contract qty';

  const contractSaveBar = (
    <div className="ppq-contract-save-bar">
      <span className="ppq-contract-save-hint">
        {readOnly
          ? 'View-only account — contract qty is read-only.'
          : 'Edit contract qty in the table, then save for this LOT (stored in Appwrite).'}
        {contractScopeLoading ? ' Loading…' : ''}
      </span>
      <div className="ppq-contract-save">
        <button
          type="button"
          className="ppq-contract-save-btn"
          onClick={handleSaveContractScope}
          disabled={readOnly || contractScopeLoading || contractSaveState === 'saving'}
        >
          {contractSaveButtonLabel}
        </button>
        {contractSaveState === 'error' && contractSaveError ? (
          <span className="ppq-contract-save-error" role="alert">
            {contractSaveError}
          </span>
        ) : null}
      </div>
    </div>
  );

  const deliveredByItemKey = useMemo(() => {
    const totals = new Map();
    if (typeof normalizePoItemsForTable !== 'function' || typeof computeRowDelivered !== 'function') {
      return totals;
    }
    (purchaseOrders || [])
      .filter((po) => poMatchesMaterialLotTab(po, lotTab))
      .forEach((po) => {
        const tableState = cableTablesState[po.id] || {};
        const items = normalizePoItemsForTable(po);
        items.forEach((item, rowIndex) => {
          const typeKey = aggregateByCableType
            ? item.itemName
            : `${po.id}::${item.itemName}::${rowIndex}`;
          const delivered = computeRowDelivered(rowIndex, tableState.deliveryData || {});
          totals.set(typeKey, (totals.get(typeKey) || 0) + delivered);
        });
      });
    return totals;
  }, [
    purchaseOrders,
    lotTab,
    cableTablesState,
    normalizePoItemsForTable,
    computeRowDelivered,
    aggregateByCableType
  ]);

  const formatQtyCell = (n) => {
    const num = parsePoLineQty(n);
    return num > 0 ? num.toLocaleString('en-US') : '—';
  };

  const contractBalance = (itemKey) => {
    const contract = parsePoLineQty(contractQtys[itemKey]);
    const delivered = deliveredByItemKey.get(itemKey) || 0;
    return contract - delivered;
  };

  const contractProgressPercent = (contractRaw, deliveredNum) => {
    const contract = parsePoLineQty(contractRaw);
    const delivered = parsePoLineQty(deliveredNum);
    if (contract <= 0) return null;
    return Math.round((delivered / contract) * 100);
  };

  const renderContractProgress = (contractRaw, deliveredNum) => {
    const pct = contractProgressPercent(contractRaw, deliveredNum);
    if (pct === null) {
      return <span className="ppq-progress__empty">—</span>;
    }
    const fillPct = Math.min(100, Math.max(0, pct));
    const over = pct > 100;
    return (
      <div className="ppq-progress">
        <div
          className="ppq-progress__track"
          role="progressbar"
          aria-valuenow={fillPct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${pct}% of contract delivered`}
        >
          <div
            className={`ppq-progress__fill${
              pct >= 100 ? ' ppq-progress__fill--complete' : ''
            }${over ? ' ppq-progress__fill--over' : ''}`}
            style={{ width: `${fillPct}%` }}
          />
        </div>
        <span className={`ppq-progress__label${over ? ' ppq-progress__label--over' : ''}`}>
          {pct}%
        </span>
      </div>
    );
  };
  const rows = useMemo(() => {
    const pos = (purchaseOrders || []).filter((po) => poMatchesMaterialLotTab(po, lotTab));

    if (aggregateByCableType) {
      const totalsByType = new Map();
      pos.forEach((po) => {
        const items =
          typeof normalizePoItemsForTable === 'function'
            ? normalizePoItemsForTable(po)
            : parsePoItemsField(po.items).map((item) => ({
                itemName:
                  correctPoItemName(
                    po?.lot,
                    item?.itemName || item?.name || item?.description || ''
                  ) || '—',
                qty: item?.qty ?? item?.quantity ?? item?.Qty ?? ''
              }));
        items.forEach((item) => {
          const cableType = item.itemName || '—';
          const rawQty = item?.qty ?? '';
          const qty =
            typeof getPoQtyNumeric === 'function'
              ? getPoQtyNumeric(po, rawQty, cableType) ?? 0
              : parsePoLineQty(rawQty);
          totalsByType.set(cableType, (totalsByType.get(cableType) || 0) + qty);
        });
      });
      return Array.from(totalsByType.entries())
        .map(([itemName, qty]) => ({
          id: itemName,
          itemKey: itemName,
          itemName,
          qty
        }))
        .sort((a, b) => a.itemName.localeCompare(b.itemName));
    }

    const out = [];
    pos.forEach((po) => {
      const supplier = String(po.supplierName || '').trim() || '—';
      const poNumber = String(po.poNumber || '').trim() || '—';
      const items =
        typeof normalizePoItemsForTable === 'function'
          ? normalizePoItemsForTable(po)
          : parsePoItemsField(po.items).map((item) => ({
              itemName: String(item?.itemName || item?.name || item?.description || '—').trim() || '—',
              qty: item?.qty ?? item?.quantity ?? item?.Qty ?? ''
            }));
      if (!items.length) {
        out.push({
          id: `${po.id}-empty`,
          itemKey: `${po.id}-empty`,
          supplier,
          poNumber,
          itemName: '—',
          qty: '',
          rowIndex: 0
        });
        return;
      }
      items.forEach((item, idx) => {
        const itemName = item.itemName || '—';
        out.push({
          id: `${po.id}-${idx}`,
          itemKey: `${po.id}::${itemName}::${idx}`,
          supplier,
          poNumber,
          itemName,
          qty: item?.qty ?? '',
          rowIndex: idx
        });
      });
    });
    return out.sort((a, b) => {
      const bySupplier = a.supplier.localeCompare(b.supplier);
      if (bySupplier !== 0) return bySupplier;
      const byPo = a.poNumber.localeCompare(b.poNumber);
      if (byPo !== 0) return byPo;
      return a.itemName.localeCompare(b.itemName);
    });
  }, [purchaseOrders, lotTab, aggregateByCableType, normalizePoItemsForTable, getPoQtyNumeric]);

  const footerTotals = useMemo(() => {
    let poQty = 0;
    let contract = 0;
    let delivered = 0;
    rows.forEach((row) => {
      poQty += parsePoLineQty(row.qty);
      contract += parsePoLineQty(contractQtys[row.itemKey]);
      delivered += deliveredByItemKey.get(row.itemKey) || 0;
    });
    return { poQty, contract, delivered, balance: contract - delivered };
  }, [rows, contractQtys, deliveredByItemKey]);

  return (
    <div className="ppq-embed">
      <div className="ppq-embed-title-bar">
        <div className="ppq-embed-title-group">
          <span className="ppq-embed-title">{title}</span>
        </div>
        <span className="ppq-embed-lot">{lotTab || '—'}</span>
      </div>
      {contractSaveBar}
      <div className="ppq-table-wrap">
        <table
          className={`ppq-table${aggregateByCableType ? ' ppq-table--cables-po-qty' : ''}`}
        >
          <thead>
            <tr>
              {aggregateByCableType ? (
                <>
                  <th className="ppq-th-type">Cable type</th>
                  <th className="ppq-th-unit">Unit</th>
                  <th className="ppq-th-num">PO qty (all POs)</th>
                  <th className="ppq-th-num">Contract qty</th>
                  <th className="ppq-th-num">Total qty delivered</th>
                  <th className="ppq-th-num">Contract balance</th>
                  <th className="ppq-th-progress">Progress</th>
                </>
              ) : (
                <>
                  <th className="ppq-th-type">Supplier</th>
                  <th className="ppq-th-type">PO No.</th>
                  <th className="ppq-th-type">Item</th>
                  <th className="ppq-th-num">Contract qty</th>
                  <th className="ppq-th-num">Total qty delivered</th>
                  <th className="ppq-th-num">Contract balance</th>
                  <th className="ppq-th-progress">Progress</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={aggregateByCableType ? 7 : 7} className="ppq-empty">
                  No purchase orders for this LOT in Purchase Order Tracking.
                </td>
              </tr>
            ) : aggregateByCableType ? (
              rows.map((row) => {
                const itemKey = row.itemKey;
                const delivered = deliveredByItemKey.get(itemKey) || 0;
                const balance = contractBalance(itemKey);
                return (
                  <tr key={row.id}>
                    <td>{row.itemName}</td>
                    <td className="ppq-unit-cell">
                      <select
                        className="ppq-unit-select"
                        value={cableUnits[itemKey] || ''}
                        onChange={(e) => handleCableUnitChange(itemKey, e.target.value)}
                        disabled={readOnly || contractScopeLoading}
                        aria-label={`Unit for ${row.itemName}`}
                      >
                        <option value="">—</option>
                        {CABLE_QTY_UNIT_OPTIONS.map((unit) => (
                          <option key={unit} value={unit}>
                            {unit}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="ppq-num">{formatQtyCell(row.qty)}</td>
                    <td className="ppq-num">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="ppq-contract-input"
                        value={contractQtys[itemKey] ?? ''}
                        onChange={(e) => handleContractQtyChange(itemKey, e.target.value)}
                        readOnly={readOnly || contractScopeLoading}
                        disabled={readOnly || contractScopeLoading}
                        aria-label={`Contract qty for ${row.itemName}`}
                      />
                    </td>
                    <td className="ppq-num">{formatQtyCell(delivered)}</td>
                    <td className={`ppq-num${balance < 0 ? ' ppq-num--negative' : ''}`}>
                      {formatQtyCell(balance)}
                    </td>
                    <td className="ppq-progress-cell">
                      {renderContractProgress(contractQtys[itemKey], delivered)}
                    </td>
                  </tr>
                );
              })
            ) : (
              rows.map((row) => {
                const itemKey = row.itemKey;
                const delivered = deliveredByItemKey.get(itemKey) || 0;
                const balance = contractBalance(itemKey);
                return (
                  <tr key={row.id}>
                    <td>{row.supplier}</td>
                    <td>
                      {row.poNumber && row.poNumber !== '—'
                        ? `PO ${String(row.poNumber).replace(/^PO\s*/i, '')}`
                        : '—'}
                    </td>
                    <td>{row.itemName}</td>
                    <td className="ppq-num">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="ppq-contract-input"
                        value={contractQtys[itemKey] ?? ''}
                        onChange={(e) => handleContractQtyChange(itemKey, e.target.value)}
                        readOnly={readOnly || contractScopeLoading}
                        disabled={readOnly || contractScopeLoading}
                        aria-label={`Contract qty for ${row.itemName}`}
                      />
                    </td>
                    <td className="ppq-num">{formatQtyCell(delivered)}</td>
                    <td className={`ppq-num${balance < 0 ? ' ppq-num--negative' : ''}`}>
                      {formatQtyCell(balance)}
                    </td>
                    <td className="ppq-progress-cell">
                      {renderContractProgress(contractQtys[itemKey], delivered)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          {rows.length > 0 ? (
            <tfoot>
              <tr className="ppq-row-totals">
                <td colSpan={aggregateByCableType ? 1 : 3}>Totals</td>
                {aggregateByCableType ? (
                  <>
                    <td className="ppq-unit-cell" aria-hidden="true" />
                    <td className="ppq-num">{formatQtyCell(footerTotals.poQty)}</td>
                  </>
                ) : null}
                <td className="ppq-num">{formatQtyCell(footerTotals.contract)}</td>
                <td className="ppq-num">{formatQtyCell(footerTotals.delivered)}</td>
                <td className={`ppq-num${footerTotals.balance < 0 ? ' ppq-num--negative' : ''}`}>
                  {formatQtyCell(footerTotals.balance)}
                </td>
                <td className="ppq-progress-cell">
                  {renderContractProgress(footerTotals.contract, footerTotals.delivered)}
                </td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
      <div className="ppq-footnote">
        Enter contract qty and unit, then click Save contract qty (stored in Appwrite per LOT)
        {contractScopeLoading ? ' (loading…)' : ''}.
        Total delivered comes from Delivery Tracking delivery columns.
        Contract balance = Contract qty − Total delivered.
      </div>
    </div>
  );
}

const filterMaterialDeliveryPos = (pos = [], query = '') => {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return pos;
  return pos.filter((po) => {
    const haystack = [
      po?.poNumber,
      po?.supplierName,
      po?.lot,
      po?.description,
      po?.subcategory
    ]
      .map((value) => String(value || '').toLowerCase())
      .join(' ');
    return haystack.includes(needle);
  });
};

/** Blank delivery columns for newly added POs — no pre-planned dates or DN numbers. */
const EMPTY_CABLE_DELIVERY_SLOTS = Array(CABLE_DELIVERY_COLUMN_COUNT).fill('');

const getCableDeliveryColIndexes = () =>
  Array.from({ length: CABLE_DELIVERY_COLUMN_COUNT }, (_, i) => CABLE_DELIVERY_COL_START + i);

/** Pad delivery dates/DN arrays to column count without overwriting already-set values. */
const padCableDeliveryArray = (arr, defaults) => {
  const base = Array.isArray(arr) && arr.length > 0 ? [...arr] : [...defaults];
  while (base.length < CABLE_DELIVERY_COLUMN_COUNT) {
    const i = base.length;
    // Prefer empty for new columns so existing set dates stay untouched
    base.push(defaults[i] !== undefined ? defaults[i] : '');
  }
  return base.slice(0, CABLE_DELIVERY_COLUMN_COUNT);
};

/**
 * Build a stable, short identifier for a Poles spreadsheet row.
 *
 * The Poles table is loaded from a static Excel file, so we can't rely on
 * Appwrite document ids — the row's identity has to come from its own
 * content. We normalise the Hamlet identity columns (Lot, Region, District,
 * Constituency, Ward, Village, Hamlet) and feed the joined string through a fast
 * 64-bit FNV-1a hash so the resulting key is a fixed-length fingerprint:
 *
 *   getPolesRowKey({ Lot: 'Lot 10', Region: 'Manyara', ... }) === 'poles_3a7f...'
 *
 * Fixed length matters because Appwrite stores `rowKey` in a utf8mb4 string
 * attribute whose unique index has a 767-byte cap (~191 utf8mb4 characters).
 * A hashed key is well under that limit regardless of how long the source
 * Hamlet strings are, and still produces the same value across reloads /
 * filtering / searching. Always use this helper so UI + Appwrite stay aligned.
 */
/** Canonical identity headers — must match import/sync scripts & Appwrite rowKey. */
const POLES_ROWKEY_HEADER_ORDER = [
  'Lot',
  'Region',
  'District',
  'Constituency',
  'Ward',
  'Village',
  'Hamlet'
];

const normalisePolesKeyPart = (value) =>
  String(value == null ? '' : value).trim().toLowerCase().replace(/\s+/g, ' ');

/** FNV-1a 32-bit; combined twice for a 64-bit hex digest (16 chars). */
const fnv1a32 = (str, seed) => {
  let hash = seed >>> 0;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};
const hashPolesIdentity = (identityString) =>
  fnv1a32(identityString, 0x811c9dc5) + fnv1a32(identityString, 0x1b873593);

/**
 * Stable Appwrite key for a Poles Excel row. Always uses
 * `POLES_ROWKEY_HEADER_ORDER` so UI lookups match backend / import scripts
 * even when the rendered column list differs.
 */
const getPolesRowKey = (row) => {
  if (!row) return '';
  const parts = POLES_ROWKEY_HEADER_ORDER.map((h) => normalisePolesKeyPart(row[h]));
  const identity = parts.join('|');
  if (!identity.replace(/\|/g, '').trim()) return '';
  return `poles_${hashPolesIdentity(identity)}`;
};

/**
 * Canonical Poles BOQ column definitions — pole types for Delivered Quantities.
 * Each entry has:
 *   - display: the canonical column header (and Delivered label base).
 *   - match: a predicate (lowercased header) that detects a matching column in
 *     the Excel headers so we can preserve the original casing/spelling.
 * Reorder this array to change the on-screen Delivered Quantities column order.
 */
const POLES_BOQ_DEFINITIONS = [
  { display: '9M Medium',  match: (h) => h.includes('9m')  && h.includes('medium') },
  { display: '10M Medium', match: (h) => h.includes('10m') && h.includes('medium') },
  { display: '10M Stout',  match: (h) => h.includes('10m') && h.includes('stout')  },
  { display: '13M Medium', match: (h) => h.includes('13m') && h.includes('medium') },
  { display: '13M Stout',  match: (h) => h.includes('13m') && h.includes('stout')  },
  { display: '12M Medium', match: (h) => h.includes('12m') && h.includes('medium') },
  { display: '12M Stout',  match: (h) => h.includes('12m') && h.includes('stout')  },
  { display: '10M Concrete', match: (h) => h.includes('10m') && h.includes('concrete') },
  { display: '13M Concrete', match: (h) => h.includes('13m') && h.includes('concrete') },
];

/** Delivered Quantities column headers: `${BOQ Display} (D)` */
const POLES_DELIVERED_BOQ_HEADERS = POLES_BOQ_DEFINITIONS.map((def) => `${def.display} (D)`);

/** True if a column header maps to any of the canonical Poles BOQ slots. */
const isPolesBoqHeader = (header) => {
  const hl = String(header || '').toLowerCase();
  return POLES_BOQ_DEFINITIONS.some((def) => def.match(hl));
};

/**
 * Excel-like freeze-pane behaviour for the Poles table.
 *
 * Lock the first `POLES_LOCKED_COLUMN_COUNT` Hamlet info columns so they stay
 * visible while Delivered Quantities and tracking columns scroll horizontally.
 * The first `POLES_HIDEABLE_LEADING_COL_COUNT` columns (Lot…Ward) can be
 * temporarily hidden to free horizontal space while Village + Hamlet stay frozen.
 */
const POLES_LOCKED_COLUMN_COUNT = 7;
const POLES_HAMLET_INFO_COL_COUNT = 7;
const POLES_HAMLET_COL_INDEX = 6; // Lot, Region, District, Constituency, Ward, Village, Hamlet
const POLES_HIDEABLE_LEADING_COL_COUNT = 5; // Lot → Ward
const POLES_HIDE_LEADING_STORAGE_KEY = 'deliveryTracking:hidePolesLeadingCols:v1';
const POLES_HAMLET_INFO_WIDTH = 80; // px — other Hamlet-info identity columns
const POLES_HAMLET_COLUMN_WIDTH = 120; // px — Hamlet names wrap; tooltip on hover for full text
const POLES_DELIVERED_COLUMN_WIDTH = 56; // px — Delivered Quantities cols
const POLES_DELIVERY_DETAILS_COLUMN_WIDTH = 100; // px — DN No., DATE DELIVERED, SUPPLIER
const POLES_PAYMENTS_COLUMN_WIDTH = 92; // px — PO No., Invoice No.
const POLES_COMMENT_COLUMN_WIDTH = 160; // px — Comment

const isPolesHamletHeader = (header) =>
  String(header || '').toLowerCase().trim() === 'hamlet';

const isPolesLotHeader = (header, lotColumnName) => {
  if (!lotColumnName) return String(header || '').toLowerCase().trim() === 'lot';
  return String(header || '').trim() === String(lotColumnName).trim();
};

/** Fixed width for Delivery Details / Payments tracking columns (scroll region). */
const getPolesTrackingColumnWidth = (header) => {
  const h = String(header || '').trim();
  if (h === 'PO No.' || h === 'Invoice No.') return POLES_PAYMENTS_COLUMN_WIDTH;
  if (h === 'Comment') return POLES_COMMENT_COLUMN_WIDTH;
  if (h === 'DN No.' || h === 'DATE DELIVERED' || h === 'SUPPLIER') return POLES_DELIVERY_DETAILS_COLUMN_WIDTH;
  if (POLES_DELIVERED_BOQ_HEADERS.includes(h) || /\(D\)\s*$/i.test(h)) return POLES_DELIVERED_COLUMN_WIDTH;
  return null;
};

/** Width of one locked Hamlet-info column. */
const polesLockedColumnWidth = (colIndex) => {
  if (colIndex === POLES_HAMLET_COL_INDEX) return POLES_HAMLET_COLUMN_WIDTH;
  if (colIndex < POLES_HAMLET_INFO_COL_COUNT) return POLES_HAMLET_INFO_WIDTH;
  return POLES_DELIVERED_COLUMN_WIDTH;
};

/** Cumulative sticky `left` skipping any hidden leading identity columns. */
const polesLockedLeftOffset = (colIndex, hiddenLeading = 0) => {
  let offset = 0;
  for (let i = 0; i < colIndex; i += 1) {
    if (i < hiddenLeading) continue;
    offset += polesLockedColumnWidth(i);
  }
  return offset;
};


/**
 * Build the inline style needed to pin a cell horizontally inside the Poles
 * table. Returns `null` for columns outside the locked block so callers can do
 * `...(polesLockedCellStyle(colIndex) || {})`.
 *
 * The cell also gets a forced width so colSpan'd headers above stay aligned
 * with the column-name + body rows. We don't set a background here because the
 * page's existing `.subtotal-cell`, `.boq-header-cell`, `.delivered-header-cell`
 * and `.delivered-data-cell` rules already provide opaque backgrounds via
 * `!important`; for unstyled body cells we fall back to white below via CSS.
 */
const polesLockedCellStyle = (
  colIndex,
  { isHeader = false, isBoundary = false, hiddenLeading = 0 } = {}
) => {
  if (colIndex >= POLES_LOCKED_COLUMN_COUNT) return null;
  if (colIndex < hiddenLeading) return null;
  const colWidth = polesLockedColumnWidth(colIndex);
  const atRightEdge = isBoundary || colIndex === POLES_LOCKED_COLUMN_COUNT - 1;
  return {
    position: 'sticky',
    left: `${polesLockedLeftOffset(colIndex, hiddenLeading)}px`,
    zIndex: isHeader ? 20 : 5,
    width: `${colWidth}px`,
    minWidth: `${colWidth}px`,
    maxWidth: `${colWidth}px`,
    boxShadow: atRightEdge ? '3px 0 6px rgba(15, 23, 42, 0.08)' : undefined,
  };
};

/**
 * Reconcile a list of Excel-derived headers against `POLES_BOQ_DEFINITIONS` so:
 *   - All canonical BOQ columns are present (synthesised if missing).
 *   - The BOQ columns sit in canonical order as a single contiguous block.
 *   - Non-BOQ columns keep their original relative order.
 * The BOQ block is anchored to wherever the first existing BOQ column lived in
 * the source data; if the source has none, the block is appended.
 */
const reconcilePolesBoqHeaders = (sourceHeaders) => {
  if (!Array.isArray(sourceHeaders) || sourceHeaders.length === 0) {
    return POLES_BOQ_DEFINITIONS.map((def) => def.display);
  }
  const firstBoqOriginalIdx = sourceHeaders.findIndex(isPolesBoqHeader);
  const nonBoq = sourceHeaders.filter((h) => !isPolesBoqHeader(h));
  const nonBoqBeforeAnchor = firstBoqOriginalIdx === -1
    ? nonBoq.length
    : sourceHeaders.slice(0, firstBoqOriginalIdx).filter((h) => !isPolesBoqHeader(h)).length;
  const canonicalBoq = POLES_BOQ_DEFINITIONS.map((def) => {
    const found = sourceHeaders.find((h) => def.match(String(h).toLowerCase()));
    return found || def.display;
  });
  return [
    ...nonBoq.slice(0, nonBoqBeforeAnchor),
    ...canonicalBoq,
    ...nonBoq.slice(nonBoqBeforeAnchor),
  ];
};

/**
 * Map a free-form LOT label (e.g. "LOT 8 (Morogoro)", "lot 10", "  LOT-10  ") to
 * a stable CSS modifier class so each delivery card can be tinted by its LOT for
 * quick visual recognition. New LOTs fall through to `--lot-other`.
 */
/**
 * Same LOT colour scheme that the Cables / Transformers / Meters cards use,
 * exposed as a hex value so we can paint the LOT ribbon directly into the
 * Poles body rows (which don't sit inside a `.po-table-container` and so
 * can't read the `--lot-accent` CSS variable from there).
 */
const getPolesLotAccentColor = (lot) => {
  const raw = String(lot || '').trim().toLowerCase();
  if (!raw) return '#64748b';
  if (/\blot[\s\-_]*8\b/.test(raw)) return '#10b981';   // LOT 8 — green
  if (/\blot[\s\-_]*10\b/.test(raw)) return '#f59e0b';  // LOT 10 — amber
  return '#64748b';                                     // other — slate
};

const lotAccentClass = (lot) => {
  const raw = String(lot || '').trim().toLowerCase();
  if (!raw) return 'po-table-container--lot-other';
  if (/\blot[\s\-_]*8\b/.test(raw)) return 'po-table-container--lot8';
  if (/\blot[\s\-_]*10\b/.test(raw)) return 'po-table-container--lot10';
  return 'po-table-container--lot-other';
};

/** Theme wrapper class for the Poles LOT tab switch (matches Material Inventory). */
const getPolesLotThemeClass = (lotName) => {
  const raw = String(lotName || '').trim().toLowerCase();
  if (/\blot[\s\-_]*8\b/.test(raw)) return 'poles-lot-theme-8';
  if (/\blot[\s\-_]*10\b/.test(raw)) return 'poles-lot-theme-10';
  return 'poles-lot-theme-default';
};

/** Resolve the LOT label column — must be the Hamlet "Lot" column, never "Total poles". */
const getPolesLotColumn = (rows) => {
  if (!rows.length) return null;
  const headers = Object.keys(rows[0]);
  const exact = headers.find((h) => String(h).trim().toLowerCase() === 'lot');
  if (exact) return exact;
  return (
    headers.find((h) => /^lot[\s\-_([]/i.test(String(h).trim())) || null
  );
};

/** True when a spreadsheet row belongs on the active LOT tab. */
const rowMatchesPolesLotTab = (row, lotColumn, tab) => {
  if (!tab) return true;
  const lotValue = lotColumn ? String(row[lotColumn] || '').trim() : '';
  const region = String(row.Region || row.REGION || '').trim().toLowerCase();
  const isTab8 = /\blot[\s\-_]*8\b/i.test(tab);
  const isTab10 = /\blot[\s\-_]*10\b/i.test(tab);

  if (isTab10) {
    if (/\blot[\s\-_]*8\b/i.test(lotValue)) return false;
    return /\blot[\s\-_]*10\b/i.test(lotValue) || region === 'manyara';
  }
  if (isTab8) {
    if (/\blot[\s\-_]*10\b/i.test(lotValue)) return false;
    return /\blot[\s\-_]*8\b/i.test(lotValue) || region === 'morogoro';
  }
  return lotValue === tab;
};

const sortPolesLotTabs = (lots) =>
  [...lots].sort((a, b) => {
    const aIs8 = /\blot[\s\-_]*8\b/i.test(a);
    const bIs8 = /\blot[\s\-_]*8\b/i.test(b);
    if (aIs8 && !bIs8) return -1;
    if (!aIs8 && bIs8) return 1;
    const aIs10 = /\blot[\s\-_]*10\b/i.test(a);
    const bIs10 = /\blot[\s\-_]*10\b/i.test(b);
    if (aIs10 && !bIs10) return -1;
    if (!aIs10 && bIs10) return 1;
    return String(a).localeCompare(String(b));
  });

/** DD-MMM-YY → yyyy-mm-dd for `<input type="date">` */
const deliveryDateToInputValue = (dateString) => {
  if (!dateString || dateString === '-') return '';
  const m = String(dateString).trim().match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2})$/i);
  if (m) {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames.findIndex((x) => x.toLowerCase() === m[2].toLowerCase()) + 1;
    if (month > 0) {
      return `20${m[3]}-${String(month).padStart(2, '0')}-${String(m[1]).padStart(2, '0')}`;
    }
  }
  try {
    const d = new Date(dateString);
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  } catch {
    /* ignore */
  }
  return '';
};

/** yyyy-mm-dd from date picker → DD-MMM-YY stored in cable table state */
const inputValueToDeliveryDate = (isoDate) => {
  if (!isoDate) return '';
  const dateObj = new Date(isoDate);
  if (Number.isNaN(dateObj.getTime())) return '';
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = dateObj.toLocaleString('en-US', { month: 'short' });
  const year = String(dateObj.getFullYear()).slice(-2);
  return `${day}-${month}-${year}`;
};

// Helper function to check if a row is blank/empty
const isBlankRow = (row) => {
  if (!row || typeof row !== 'object') return true;
  const values = Object.values(row);
  
  // Check if all values are empty, null, undefined, or just whitespace
  // A row is blank if ALL values are empty/null/undefined/whitespace/dash
  const hasAnyValue = values.some(value => {
    if (value === null || value === undefined) return false;
    
    // Convert to string and trim
    const strValue = String(value).trim();
    
    // Consider it a value if it's not empty and not just a dash
    // Note: We don't exclude 0 as it could be a valid data point
    return strValue !== '' && strValue !== '-';
  });
  
  // If no meaningful value found, it's a blank row
  return !hasAnyValue;
};

function DeliveryTracking() {
  const authSession = useAuthSession();
  const isPolesLot10Only = isPolesLot10OnlyRole(authSession);
  const isLot10Locked = isLot10ScopedSession(authSession) && !isPolesLot10Only;
  const isReadOnly = isReadOnlySession(authSession);
  const preferLot10 = isPolesLot10Only || isLot10Locked;
  const [polesData, setPolesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [polesLotTab, setPolesLotTab] = useState('');
  /** Poles Tracking Data panel: hamlet delivery table or PO quantities table. */
  const [polesPanel, setPolesPanel] = useState('delivery'); // 'delivery' | 'po-quantities'
  /** Material delivery (cables, concrete, transformers, meters): delivery grids vs PO quantities. */
  const [materialPanel, setMaterialPanel] = useState('delivery');
  const [materialLotTab, setMaterialLotTab] = useState('LOT 8');
  const [polesPurchaseOrders, setPolesPurchaseOrders] = useState([]);
  const [onlyDeliveredFilled, setOnlyDeliveredFilled] = useState(false);
  const [hideLeadingPolesColumns, setHideLeadingPolesColumns] = useState(() => {
    try {
      return window.localStorage.getItem(POLES_HIDE_LEADING_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [activeTable, setActiveTable] = useState('poles'); // 'poles', 'cables', 'transformers', 'meters'
  const [isStatsVisible, setIsStatsVisible] = useState(true);
  const statsGridRef = useRef(null);
  const contentSectionRef = useRef(null);
  
  // State for delivery dates
  // eslint-disable-next-line no-unused-vars
  const [deliveryDates, setDeliveryDates] = useState([
    '31-May-25',
    '11-Jun-25',
    '25-Jun-25',
    '18-Jul-25',
    '31-May-25',
    '11-Jun-25',
    '25-Jun-25',
    '18-Jul-25'
  ]);
  
  // State for delivery numbers (8940, 8941, 54727, 54859, etc.)
  const [deliveryNumbers, setDeliveryNumbers] = useState([
    '8940',
    '8941',
    '54727',
    '54859',
    '8940',
    '8941',
    '54727',
    '54859'
  ]);
  
  // State for second table delivery dates
  // eslint-disable-next-line no-unused-vars
  const [deliveryDates2, setDeliveryDates2] = useState([
    '31-May-25',
    '11-Jun-25',
    '25-Jun-25',
    '18-Jul-25',
    '31-May-25',
    '11-Jun-25',
    '25-Jun-25',
    '18-Jul-25'
  ]);
  
  // State for second table delivery numbers
  const [deliveryNumbers2, setDeliveryNumbers2] = useState([
    '8940',
    '8941',
    '54727',
    '54859',
    '8940',
    '8941',
    '54727',
    '54859'
  ]);
  
  // Handle delivery number change
  // eslint-disable-next-line no-unused-vars
  const handleDeliveryNumberChange = (index, value) => {
    const next = String(value ?? '').trim();
    const key = next.toLowerCase();
    if (next && next !== '-' && deliveryNumbers.some((n, i) => i !== index && String(n ?? '').trim().toLowerCase() === key)) {
      window.alert(`DN "${next}" is already used. Enter a unique DN number.`);
      return;
    }
    setDeliveryNumbers(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };
  
  // Handle second table delivery number change
  // eslint-disable-next-line no-unused-vars
  const handleDeliveryNumberChange2 = (index, value) => {
    const next = String(value ?? '').trim();
    const key = next.toLowerCase();
    if (next && next !== '-' && deliveryNumbers2.some((n, i) => i !== index && String(n ?? '').trim().toLowerCase() === key)) {
      window.alert(`DN "${next}" is already used. Enter a unique DN number.`);
      return;
    }
    setDeliveryNumbers2(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };
  
  // Handle date change
  // eslint-disable-next-line no-unused-vars
  const handleDateChange = (dateIndex, newDate) => {
    setDeliveryDates(prev => {
      const updated = [...prev];
      updated[dateIndex] = newDate;
      return updated;
    });
  };
  
  // Handle second table date change
  // eslint-disable-next-line no-unused-vars
  const handleDateChange2 = (dateIndex, newDate) => {
    setDeliveryDates2(prev => {
      const updated = [...prev];
      updated[dateIndex] = newDate;
      return updated;
    });
  };
  
  // Format date for display (DD-MMM-YY)
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    // If it's already in the format DD-MMM-YY, return as is
    if (dateString.match(/^\d{2}-[A-Z]{3}-\d{2}$/)) {
      return dateString;
    }
    // Try to parse and format
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const day = String(date.getDate()).padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'short' });
      const year = String(date.getFullYear()).slice(-2);
      return `${day}-${month}-${year}`;
    } catch {
      return dateString;
    }
  };
  
  // State for editable delivery cells
  const [deliveryData, setDeliveryData] = useState({
    // Row index -> column index -> value
    0: { 3: '10,598', 4: '9,900', 5: '-', 6: '10,401', 7: '10,598', 8: '9,900', 9: '-', 10: '10,401' },
    1: { 3: '-', 4: '-', 5: '23,990', 6: '-', 7: '-', 8: '-', 9: '23,990', 10: '-' },
    2: { 3: '-', 4: '-', 5: '-', 6: '-', 7: '-', 8: '-', 9: '-', 10: '-' },
    3: { 3: '-', 4: '970', 5: '-', 6: '-', 7: '-', 8: '970', 9: '-', 10: '-' },
    4: { 3: '-', 4: '-', 5: '-', 6: '-', 7: '-', 8: '-', 9: '-', 10: '-' }
  });
  
  // State for second table editable delivery cells
  // eslint-disable-next-line no-unused-vars
  const [deliveryData2, setDeliveryData2] = useState({
    // Row index -> column index -> value
    0: { 3: '10,598', 4: '9,900', 5: '-', 6: '10,401', 7: '10,598', 8: '9,900', 9: '-', 10: '10,401' },
    1: { 3: '-', 4: '-', 5: '23,990', 6: '-', 7: '-', 8: '-', 9: '23,990', 10: '-' },
    2: { 3: '-', 4: '-', 5: '-', 6: '-', 7: '-', 8: '-', 9: '-', 10: '-' },
    3: { 3: '-', 4: '970', 5: '-', 6: '-', 7: '-', 8: '970', 9: '-', 10: '-' },
    4: { 3: '-', 4: '-', 5: '-', 6: '-', 7: '-', 8: '-', 9: '-', 10: '-' }
  });
  
  // State for meters table delivery dates
  // eslint-disable-next-line no-unused-vars
  const [deliveryDates3, setDeliveryDates3] = useState([
    '31-May-25',
    '11-Jun-25',
    '25-Jun-25',
    '18-Jul-25',
    '31-May-25',
    '11-Jun-25',
    '25-Jun-25',
    '18-Jul-25'
  ]);
  
  // State for meters table delivery numbers
  const [deliveryNumbers3, setDeliveryNumbers3] = useState([
    '8940',
    '8941',
    '54727',
    '54859',
    '8940',
    '8941',
    '54727',
    '54859'
  ]);
  
  // State for meters table editable delivery cells
  // eslint-disable-next-line no-unused-vars
  const [deliveryData3, setDeliveryData3] = useState({
    // Row index -> column index -> value
    0: { 3: '-', 4: '-', 5: '-', 6: '-', 7: '-', 8: '-', 9: '-', 10: '-' },
    1: { 3: '-', 4: '-', 5: '-', 6: '-', 7: '-', 8: '-', 9: '-', 10: '-' }
  });

  /**
   * Editable cell values for the Poles table, keyed by the stable `rowKey`
   * (see `getPolesRowKey`). Shape: `{ [rowKey]: { [columnName]: value } }`.
   *
   * Switched from index-based to key-based so:
   *   - edits survive LOT / search filtering (the filtered index changes but
   *     the row's identity doesn't),
   *   - we can round-trip through the `PolesDeliveriesSGM` collection by
   *     persisting one document per rowKey.
   */
  const [editableValues, setEditableValues] = useState({});
  // State to track which input is currently focused: "rowKey-columnName"
  const [focusedInput, setFocusedInput] = useState(null);
  /**
   * Has the initial Appwrite fetch of persisted Poles edits finished? We use
   * this to suppress save-on-mount writes (otherwise the first render's empty
   * `editableValues` would overwrite the data we just loaded).
   */
  const [polesEditsLoaded, setPolesEditsLoaded] = useState(false);
  /**
   * Excel-style cross-hair hover for the Poles table. We store the hovered
   * (rowIndex, colIndex) and render a single dynamic <style> block in the
   * table wrapper — that way the 14,000-cell grid doesn't re-render on every
   * mouse move; only the <style> element diff updates the highlight.
   */
  const [polesHoverRow, setPolesHoverRow] = useState(null);
  const [polesHoverCol, setPolesHoverCol] = useState(null);
  /**
   * Debounce timers + last-known snapshot per rowKey, so we coalesce bursts
   * of keystrokes / status changes into a single `upsertPolesEdit` call ~600ms
   * after the user stops editing. Keeps the Appwrite write-rate sane.
   */
  const polesUpsertTimers = useRef({});
  const polesUpsertSnapshots = useRef({});
  const polesDataRef = useRef([]);
  const lotColumnRef = useRef('');
  
  // State for poles suppliers (filtered from suppliers list)
  const [polesSuppliers, setPolesSuppliers] = useState([]);
  const [cablePurchaseOrders, setCablePurchaseOrders] = useState([]);
  const [cablesLoading, setCablesLoading] = useState(false);
  const [concretePurchaseOrders, setConcretePurchaseOrders] = useState([]);
  const [concreteLoading, setConcreteLoading] = useState(false);
  const [transformerPurchaseOrders, setTransformerPurchaseOrders] = useState([]);
  const [transformersLoading, setTransformersLoading] = useState(false);
  const [meterPurchaseOrders, setMeterPurchaseOrders] = useState([]);
  const [metersLoading, setMetersLoading] = useState(false);
  const [cableTablesState, setCableTablesState] = useState({});
  const cableDeliveriesHydratedRef = useRef(false);
  const cableSaveDebounceRef = useRef(null);
  const cableTablesStateRef = useRef({});

  useEffect(() => {
    cableTablesStateRef.current = cableTablesState;
  }, [cableTablesState]);

  const [expandedMaterialPoIds, setExpandedMaterialPoIds] = useState(() => new Set());
  const [activeDeliveryEditor, setActiveDeliveryEditor] = useState(null);
  const [deliveryEditorValue, setDeliveryEditorValue] = useState('');
  const [deliveryEditorDateValue, setDeliveryEditorDateValue] = useState('');
  const [activeDeliveryNumberEditor, setActiveDeliveryNumberEditor] = useState(null);
  const [deliveryNumberEditorValue, setDeliveryNumberEditorValue] = useState('');
  
  // Handle delivery cell change
  // eslint-disable-next-line no-unused-vars
  const handleDeliveryCellChange = (rowIndex, colIndex, value) => {
    setDeliveryData(prev => ({
      ...prev,
      [rowIndex]: {
        ...prev[rowIndex],
        [colIndex]: value
      }
    }));
  };

  const normalizeItems = (itemsValue) => {
    if (Array.isArray(itemsValue)) return itemsValue;
    if (typeof itemsValue === 'string') {
      try {
        const parsed = JSON.parse(itemsValue);
        if (Array.isArray(parsed)) return parsed;
        if (typeof parsed === 'string') {
          const parsedAgain = JSON.parse(parsed);
          return Array.isArray(parsedAgain) ? parsedAgain : [];
        }
      } catch (error) {
        return [];
      }
    }
    return [];
  };

  const buildInitialDeliveryDataForItems = (items = []) => {
    const emptyCols = getCableDeliveryColIndexes().reduce((cols, colIndex) => {
      cols[colIndex] = '-';
      return cols;
    }, {});
    const normalizedItems = normalizeItems(items);
    return normalizedItems.reduce((acc, _item, rowIndex) => {
      acc[rowIndex] = { ...emptyCols };
      return acc;
    }, {});
  };

  /** Material concrete-pole POs — items/description/subcategory override supplier heuristics. */
  const isConcretePolesPurchaseOrder = (po) => {
    const category = String(po?.category || '').toLowerCase();
    if (category !== 'material') return false;

    const subcategory = String(po?.subcategory || '').toLowerCase();
    const description = String(po?.description || '').toLowerCase();
    const items = normalizeItems(po?.items);
    const itemNames = items.map((item) =>
      String(item?.itemName || item?.name || item?.description || '').toLowerCase()
    );
    const hasConcreteItem = itemNames.some((name) => name.includes('concrete'));
    const hasCableItem = itemNames.some(
      (name) => name.includes('cable') || name.includes('cables')
    );
    const hasTransformerItem = itemNames.some((name) => name.includes('transformer'));
    const hasMeterItem = itemNames.some((name) => name.includes('meter'));

    const isExplicitlyConcrete =
      subcategory.includes('concrete') ||
      description.includes('concrete') ||
      hasConcreteItem;

    if (isExplicitlyConcrete) return true;

    const isExplicitlyOtherMaterial =
      subcategory.includes('cable') ||
      subcategory.includes('transformer') ||
      subcategory.includes('meter') ||
      subcategory.includes('wooden') ||
      subcategory.includes('fitting') ||
      hasCableItem ||
      hasTransformerItem ||
      hasMeterItem;

    if (isExplicitlyOtherMaterial) return false;

    const supplier = String(po?.supplierName || '').toLowerCase();
    if (supplier.includes('everwell')) return true;

    return false;
  };

  const DEFAULT_CONCRETE_POLE_ITEMS = [
    { itemName: '10M Concrete', qty: '' },
    { itemName: '13M Concrete', qty: '' }
  ];

  const isCablePurchaseOrder = (po) => {
    const category = String(po?.category || '').toLowerCase();
    if (category !== 'material') return false;

    const subcategory = String(po?.subcategory || '').toLowerCase();
    const description = String(po?.description || '').toLowerCase();
    const items = normalizeItems(po?.items);
    const hasCableItem = items.some(
      (item) =>
        String(item?.itemName || '').toLowerCase().includes('cable') ||
        String(item?.itemName || '').toLowerCase().includes('cables')
    );

    return (
      subcategory.includes('cable') ||
      subcategory.includes('cables') ||
      description.includes('cable') ||
      description.includes('cables') ||
      hasCableItem
    );
  };

  const isTransformerPurchaseOrder = (po) => {
    const category = String(po?.category || '').toLowerCase();
    const subcategory = String(po?.subcategory || '').toLowerCase();
    const description = String(po?.description || '').toLowerCase();
    const items = normalizeItems(po?.items);
    const hasTransformerItem = items.some((item) =>
      String(item?.itemName || '').toLowerCase().includes('transformer')
    );
    return (
      category === 'material' &&
      (subcategory.includes('transformer') ||
        description.includes('transformer') ||
        hasTransformerItem)
    );
  };

  const isMeterPurchaseOrder = (po) => {
    const category = String(po?.category || '').toLowerCase();
    const subcategory = String(po?.subcategory || '').toLowerCase();
    const description = String(po?.description || '').toLowerCase();
    const items = normalizeItems(po?.items);
    const hasMeterItem = items.some((item) =>
      String(item?.itemName || '').toLowerCase().includes('meter')
    );
    return (
      category === 'material' &&
      (subcategory.includes('meter') ||
        description.includes('meter') ||
        hasMeterItem)
    );
  };

  const normalizePoItemsForTable = (po) => {
    const parsedItems = normalizeItems(po?.items);
    const normalized = parsedItems
      .map((item) => {
        const rawName =
          item?.itemName ||
          item?.name ||
          item?.item ||
          item?.description ||
          '';
        return {
          itemName: correctPoItemName(po?.lot, rawName),
          qty:
            item?.qty ??
            item?.quantity ??
            item?.poQty ??
            item?.po_qty ??
            ''
        };
      })
      .filter((item) => item.itemName || item.qty !== '');

    if (normalized.length > 0) return normalized;

    // Concrete POs often ship with description only — seed 10M / 13M Concrete lines.
    if (isConcretePolesPurchaseOrder(po)) {
      return DEFAULT_CONCRETE_POLE_ITEMS.map((row) => ({ ...row }));
    }

    // Fallback to PO-level description if items are missing
    return [{ itemName: po?.description || 'Item', qty: '' }];
  };

  const shouldMultiplyPoQtyBy1000 = (poNumber = '') => {
    const value = String(poNumber || '');
    return value.includes('551001171') || value.includes('551001172');
  };

  /** MCL cable POs store some lines in km (×1000 → m); 100sqmm ACSR is already final qty. */
  const applyPoQtyThousandsRule = (poNumber, qtyValue, itemName = '') => {
    if (qtyValue === null || qtyValue === undefined || qtyValue === '') return null;
    const numeric = parseFloat(String(qtyValue).replace(/,/g, ''));
    if (Number.isNaN(numeric)) return null;
    if (shouldMultiplyPoQtyBy1000(poNumber) && !is100sqmmAcsrItemName(itemName)) {
      return numeric * 1000;
    }
    return numeric;
  };

  const getDisplayPoQty = (po, qtyValue, itemName = '') => {
    if (qtyValue === null || qtyValue === undefined || qtyValue === '') return '';
    const adjusted = applyPoQtyThousandsRule(po?.poNumber, qtyValue, itemName);
    if (adjusted === null) return String(qtyValue);
    return adjusted.toLocaleString('en-US', { maximumFractionDigits: 3 });
  };

  /** Numeric PO qty (M) for a line — same rules as PO Qty column (incl. ×1000 PO numbers). */
  const getPoQtyNumeric = (po, qtyValue, itemName = '') =>
    applyPoQtyThousandsRule(po?.poNumber, qtyValue, itemName);

  /** PO Qty balance (M) = PO qty − total delivered for the row. */
  const getPoQtyBalanceDisplay = (po, item, rowIndex, deliveryData) => {
    const poQtyNum = getPoQtyNumeric(po, item?.qty, item?.itemName);
    if (poQtyNum === null) return '-';
    const totalDelivered = calculateRowTotal(rowIndex, deliveryData);
    const balance = poQtyNum - totalDelivered;
    return balance.toLocaleString('en-US', { maximumFractionDigits: 3 });
  };

  const handleCablesDateChange = (poId, index, value) => {
    setCableTablesState((prev) => {
      const next = {
        ...prev,
        [poId]: {
          ...(prev[poId] || {}),
          deliveryDates: padCableDeliveryArray(prev[poId]?.deliveryDates, EMPTY_CABLE_DELIVERY_SLOTS).map((item, i) =>
            i === index ? value : item
          )
        }
      };
      cableTablesStateRef.current = next;
      return next;
    });
  };

  const normalizeDeliveryNoteKey = (value) => String(value ?? '').trim().toLowerCase();

  const isBlankDeliveryNote = (value) => {
    const t = String(value ?? '').trim();
    return t === '' || t === '-' || t === '—';
  };

  /** True when this DN is already used on another delivery column of the same PO. */
  const isDuplicateCableDeliveryNote = (poId, index, value, tablesState = cableTablesState) => {
    if (isBlankDeliveryNote(value)) return false;
    const key = normalizeDeliveryNoteKey(value);
    const nums = padCableDeliveryArray(tablesState?.[poId]?.deliveryNumbers, EMPTY_CABLE_DELIVERY_SLOTS);
    return nums.some(
      (n, i) => i !== index && !isBlankDeliveryNote(n) && normalizeDeliveryNoteKey(n) === key
    );
  };

  const handleCablesDeliveryNumberChange = (poId, index, value) => {
    const next = value === undefined || value === null ? '-' : String(value).trim() === '' ? '-' : String(value).trim();
    if (isDuplicateCableDeliveryNote(poId, index, next)) {
      return false;
    }
    setCableTablesState((prev) => {
      const nextState = {
        ...prev,
        [poId]: {
          ...(prev[poId] || {}),
          deliveryNumbers: padCableDeliveryArray(prev[poId]?.deliveryNumbers, EMPTY_CABLE_DELIVERY_SLOTS).map((item, i) =>
            i === index ? next : item
          )
        }
      };
      cableTablesStateRef.current = nextState;
      return nextState;
    });
    return true;
  };

  /** Store cable delivery qty with thousands separators (data/Appwrite); parse still strips commas. */
  const normalizeCableDeliveryQtyForStorage = (raw) => {
    const t = String(raw ?? '').trim();
    if (t === '' || t === '-') return '-';
    const n = parseFloat(t.replace(/,/g, ''));
    if (Number.isNaN(n)) return t;
    return n.toLocaleString('en-US', { maximumFractionDigits: 10 });
  };

  /** Apply comma formatting to every cell in deliveryData (for Appwrite/localStorage). */
  const normalizeDeliveryDataCommas = (deliveryData) => {
    if (!deliveryData || typeof deliveryData !== 'object') return deliveryData;
    const out = {};
    Object.entries(deliveryData).forEach(([rowKey, row]) => {
      if (!row || typeof row !== 'object') {
        out[rowKey] = row;
        return;
      }
      out[rowKey] = {};
      Object.entries(row).forEach(([colKey, cell]) => {
        out[rowKey][colKey] = normalizeCableDeliveryQtyForStorage(cell);
      });
    });
    return out;
  };

  const handleCablesDeliveryCellChange = (poId, rowIndex, colIndex, value) => {
    const stored = normalizeCableDeliveryQtyForStorage(value);
    setCableTablesState(prev => ({
      ...prev,
      [poId]: {
        ...(prev[poId] || {}),
        deliveryData: {
          ...(prev[poId]?.deliveryData || {}),
          [rowIndex]: {
            ...(prev[poId]?.deliveryData?.[rowIndex] || {}),
            [colIndex]: stored
          }
        }
      }
    }));
  };

  const openDeliveryCellEditor = (poId, rowIndex, colIndex, currentValue = '', deliveryDatesForPo = null) => {
    setActiveDeliveryEditor({ poId, rowIndex, colIndex });
    const raw = currentValue === '-' ? '' : String(currentValue ?? '').trim();
    setDeliveryEditorValue(raw.replace(/,/g, ''));
    const dateIndex = colIndex - 3;
    const dates =
      deliveryDatesForPo?.length > 0 ? deliveryDatesForPo : EMPTY_CABLE_DELIVERY_SLOTS;
    const columnDate = dates[dateIndex] ?? EMPTY_CABLE_DELIVERY_SLOTS[dateIndex] ?? '';
    setDeliveryEditorDateValue(deliveryDateToInputValue(columnDate));
  };

  const closeDeliveryCellEditor = () => {
    setActiveDeliveryEditor(null);
    setDeliveryEditorValue('');
    setDeliveryEditorDateValue('');
  };

  const saveDeliveryCellEditor = () => {
    if (!activeDeliveryEditor) return;
    const { poId, rowIndex, colIndex } = activeDeliveryEditor;
    handleCablesDeliveryCellChange(poId, rowIndex, colIndex, deliveryEditorValue.trim() === '' ? '-' : deliveryEditorValue);
    const dateIndex = colIndex - 3;
    if (dateIndex >= 0 && dateIndex < CABLE_DELIVERY_COLUMN_COUNT && deliveryEditorDateValue) {
      handleCablesDateChange(poId, dateIndex, inputValueToDeliveryDate(deliveryEditorDateValue));
    }
    closeDeliveryCellEditor();
  };

  const openDeliveryNumberEditor = (poId, index, currentValue = '') => {
    setActiveDeliveryNumberEditor({ poId, index });
    setDeliveryNumberEditorValue(String(currentValue || ''));
  };

  const closeDeliveryNumberEditor = () => {
    setActiveDeliveryNumberEditor(null);
    setDeliveryNumberEditorValue('');
  };

  const saveDeliveryNumberEditor = () => {
    if (!activeDeliveryNumberEditor) return;
    const { poId, index } = activeDeliveryNumberEditor;
    const next = deliveryNumberEditorValue.trim() === '' ? '-' : deliveryNumberEditorValue.trim();
    if (isDuplicateCableDeliveryNote(poId, index, next)) {
      window.alert(`DN "${next}" is already used on another delivery column for this PO. Enter a unique DN number.`);
      return;
    }
    handleCablesDeliveryNumberChange(poId, index, next);
    closeDeliveryNumberEditor();
  };

  const hasQtyInDeliveryColumn = (deliveryData, colIndex) => {
    const rows = Object.values(deliveryData || {});
    return rows.some((row) => {
      const value = row?.[colIndex];
      if (value === null || value === undefined) return false;
      const text = String(value).trim();
      if (text === '' || text === '-') return false;
      const parsed = parseFloat(text.replace(/,/g, ''));
      return !Number.isNaN(parsed) && parsed > 0;
    });
  };
  
  // Handle second table delivery cell change
  // eslint-disable-next-line no-unused-vars
  const handleDeliveryCellChange2 = (rowIndex, colIndex, value) => {
    setDeliveryData2(prev => ({
      ...prev,
      [rowIndex]: {
        ...prev[rowIndex],
        [colIndex]: value
      }
    }));
  };
  
  // Handle meters table delivery number change
  // eslint-disable-next-line no-unused-vars
  const handleDeliveryNumberChange3 = (index, value) => {
    const next = String(value ?? '').trim();
    const key = next.toLowerCase();
    if (next && next !== '-' && deliveryNumbers3.some((n, i) => i !== index && String(n ?? '').trim().toLowerCase() === key)) {
      window.alert(`DN "${next}" is already used. Enter a unique DN number.`);
      return;
    }
    setDeliveryNumbers3(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };
  
  // Handle meters table date change
  // eslint-disable-next-line no-unused-vars
  const handleDateChange3 = (dateIndex, newDate) => {
    setDeliveryDates3(prev => {
      const updated = [...prev];
      updated[dateIndex] = newDate;
      return updated;
    });
  };
  
  // Handle meters table delivery cell change
  // eslint-disable-next-line no-unused-vars
  const handleDeliveryCellChange3 = (rowIndex, colIndex, value) => {
    setDeliveryData3(prev => ({
      ...prev,
      [rowIndex]: {
        ...prev[rowIndex],
        [colIndex]: value
      }
    }));
  };
  
  // Calculate total delivery for a row
  const calculateRowTotal = (rowIndex, data = deliveryData) => {
    const rowData = data[rowIndex] || {};
    const deliveryCols = getCableDeliveryColIndexes();
    let total = 0;
    
    deliveryCols.forEach(colIndex => {
      const value = rowData[colIndex];
      if (value && value !== '-' && value !== '') {
        // Remove commas and parse as number
        const numValue = parseFloat(value.toString().replace(/,/g, ''));
        if (!isNaN(numValue)) {
          total += numValue;
        }
      }
    });
    
    return total;
  };
  
  // Calculate percentage delivered: (Total Delivery / PO Qty's (M)) * 100
  const calculatePercentDelivered = (rowIndex, poQty, data = deliveryData) => {
    const totalDelivery = calculateRowTotal(rowIndex, data);
    
    if (totalDelivery === 0 || !poQty) {
      return '0%';
    }
    
    // Parse PO Qty (remove commas)
    const poQtyNum = parseFloat(poQty.toString().replace(/,/g, ''));
    
    if (isNaN(poQtyNum) || poQtyNum === 0) {
      return '0%';
    }
    
    const percentage = (totalDelivery / poQtyNum) * 100;
    return `${Math.round(percentage)}%`;
  };
  
  // Format total for display
  const formatTotal = (total) => {
    return total > 0 ? total.toLocaleString() : '-';
  };

  const toggleMaterialPoExpanded = (poId) => {
    setExpandedMaterialPoIds((prev) => {
      const next = new Set(prev);
      if (next.has(poId)) next.delete(poId);
      else next.add(poId);
      return next;
    });
  };

  const getMaterialPoSummary = (po, rowData, tableState) => {
    let totalDelivered = 0;
    let totalPoQty = 0;
    rowData.forEach((item, rowIndex) => {
      const poQtyNum = getPoQtyNumeric(po, item?.qty, item?.itemName);
      if (poQtyNum !== null) totalPoQty += poQtyNum;
      totalDelivered += calculateRowTotal(rowIndex, tableState.deliveryData);
    });
    const pctDelivered =
      totalPoQty > 0 ? Math.round((totalDelivered / totalPoQty) * 100) : 0;
    const filledDns = padCableDeliveryArray(
      tableState.deliveryNumbers,
      EMPTY_CABLE_DELIVERY_SLOTS
    ).filter((number) => String(number || '').trim()).length;
    return {
      itemCount: rowData.length,
      pctDelivered,
      filledDns
    };
  };

  /** Excel worksheet name: max 31 chars; no \ / * ? : [ ] */
  const sanitizeExcelSheetName = (raw) => {
    let s = String(raw || 'PO')
      .replace(/[:\\/*?[\]]/g, '-')
      .replace(/\s+/g, ' ')
      .trim();
    if (s.length > 31) s = s.slice(0, 31).trim();
    return s || 'PO';
  };

  /** Resolve cable grid state for export (matches hydrate priority; prefers live React state when present). */
  const resolveCableTableStateForExport = (po, remoteMap, localMap) => {
    if (cableTablesState[po.id]) {
      return cableTablesState[po.id];
    }
    const fromRemote = remoteMap[po.id];
    const fromLocal = localMap[po.id];
    const hasRemoteGrid =
      fromRemote &&
      (fromRemote.deliveryDates?.length > 0 ||
        Object.keys(fromRemote.deliveryData || {}).length > 0);
    if (hasRemoteGrid) {
      return {
        deliveryDates: padCableDeliveryArray(
          fromRemote.deliveryDates?.length > 0 ? fromRemote.deliveryDates : EMPTY_CABLE_DELIVERY_SLOTS,
          EMPTY_CABLE_DELIVERY_SLOTS
        ),
        deliveryNumbers: padCableDeliveryArray(
          fromRemote.deliveryNumbers?.length > 0 ? fromRemote.deliveryNumbers : EMPTY_CABLE_DELIVERY_SLOTS,
          EMPTY_CABLE_DELIVERY_SLOTS
        ),
        deliveryData: normalizeDeliveryDataCommas(
          fromRemote.deliveryData && Object.keys(fromRemote.deliveryData).length > 0
            ? fromRemote.deliveryData
            : buildInitialDeliveryDataForItems(po.items || [])
        )
      };
    }
    if (fromLocal) {
      return {
        deliveryDates: padCableDeliveryArray(fromLocal.deliveryDates, EMPTY_CABLE_DELIVERY_SLOTS),
        deliveryNumbers: padCableDeliveryArray(fromLocal.deliveryNumbers, EMPTY_CABLE_DELIVERY_SLOTS),
        deliveryData: normalizeDeliveryDataCommas(
          fromLocal.deliveryData && Object.keys(fromLocal.deliveryData).length > 0
            ? fromLocal.deliveryData
            : buildInitialDeliveryDataForItems(po.items || [])
        )
      };
    }
    return {
      deliveryDates: [...EMPTY_CABLE_DELIVERY_SLOTS],
      deliveryNumbers: [...EMPTY_CABLE_DELIVERY_SLOTS],
      deliveryData: buildInitialDeliveryDataForItems(po.items || [])
    };
  };

  const exportDeliveriesTrackingExcel = async ({
    requiredTab,
    filterPo,
    sheetTitle,
    filePrefix,
    noDataMessage,
    tabLabel,
    fetchRemoteMap = fetchCableDeliveriesStateMap,
    storageKey = CABLE_TABLES_STORAGE_KEY
  }) => {
    if (activeTable !== requiredTab) {
      window.alert(`Switch to the ${tabLabel} tab, then click Export Excel.`);
      return;
    }

    try {
      const purchaseOrders = await getAllPurchaseOrders();
      const filteredPOs = (purchaseOrders || []).filter(filterPo);
      if (!filteredPOs.length) {
        window.alert(noDataMessage);
        return;
      }

      let remoteMap = {};
      try {
        remoteMap = await fetchRemoteMap();
      } catch (err) {
        console.warn('Delivery tracking remote map unavailable for export:', err);
      }

      let localMap = {};
      try {
        const raw = localStorage.getItem(storageKey);
        if (raw) localMap = JSON.parse(raw) || {};
      } catch (_) {
        localMap = {};
      }

      const wb = XLSX.utils.book_new();
      const usedSheetNames = new Set();
      const deliveryCols = getCableDeliveryColIndexes();

      filteredPOs.forEach((po, poIdx) => {
        const tableState = resolveCableTableStateForExport(po, remoteMap, localMap);
        const rowData = normalizePoItemsForTable(po);
        const dns = padCableDeliveryArray(tableState.deliveryNumbers, EMPTY_CABLE_DELIVERY_SLOTS);
        const dates = padCableDeliveryArray(tableState.deliveryDates, EMPTY_CABLE_DELIVERY_SLOTS);

        const aoa = [];
        aoa.push([sheetTitle]);
        aoa.push(['Supplier', po.supplierName || '-']);
        aoa.push(['Order No', po.poNumber || '-']);
        aoa.push(['Lot name', po.lot || '-']);
        aoa.push(['Subcategory', po.subcategory || '-']);
        aoa.push([]);
        aoa.push([
          '',
          '',
          '',
          'Deliveries',
          ...Array(CABLE_DELIVERY_COLUMN_COUNT - 1).fill(''),
          '',
          '',
          ''
        ]);
        aoa.push([
          '',
          '',
          '',
          ...dns.map((dn) =>
            dn !== undefined && dn !== null && String(dn).trim() !== '' && String(dn).trim() !== '-'
              ? String(dn).trim()
              : ''
          ),
          '',
          '',
          ''
        ]);
        aoa.push([
          'S/No',
          'Items',
          "PO Qty's (M)",
          ...dates.map((date) => formatDateForDisplay(date || '')),
          'Total Delivery',
          '% Delivered',
          'PO Qty balance (M)'
        ]);

        rowData.forEach((item, rowIndex) => {
          const poQty = getDisplayPoQty(po, item?.qty, item?.itemName);
          const dd = tableState.deliveryData || {};
          const cells = deliveryCols.map((ci) => {
            const v = dd[rowIndex]?.[ci];
            if (v === undefined || v === null || v === '') return '';
            const t = String(v).trim();
            return t === '-' ? '' : t;
          });
          const total = calculateRowTotal(rowIndex, dd);
          const pct = calculatePercentDelivered(rowIndex, poQty, dd);
          const bal = getPoQtyBalanceDisplay(po, item, rowIndex, dd);
          aoa.push([rowIndex + 1, item?.itemName || '-', poQty || '-', ...cells, formatTotal(total), pct, bal]);
        });

        const ws = XLSX.utils.aoa_to_sheet(aoa);
        let baseName = po.poNumber ? `PO ${po.poNumber}` : `PO_${poIdx + 1}`;
        let sheetName = sanitizeExcelSheetName(baseName);
        let n = 1;
        while (usedSheetNames.has(sheetName)) {
          sheetName = sanitizeExcelSheetName(`${baseName}_${n}`);
          n += 1;
        }
        usedSheetNames.add(sheetName);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });

      const dateLabel = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `${filePrefix}_${dateLabel}.xlsx`);
    } catch (err) {
      console.error(err);
      window.alert(err?.message || `Failed to export ${tabLabel.toLowerCase()} deliveries.`);
    }
  };

  const handleExportCableDeliveriesExcel = () =>
    exportDeliveriesTrackingExcel({
      requiredTab: 'cables',
      tabLabel: 'Cables',
      filterPo: isCablePurchaseOrder,
      sheetTitle: 'Cable Delivery Tracking',
      filePrefix: 'Cable_Deliveries_All_POs',
      noDataMessage: 'No cable purchase orders to export.'
    });

  const handleExportConcretePolesDeliveriesExcel = () =>
    exportDeliveriesTrackingExcel({
      requiredTab: 'concrete-poles',
      tabLabel: 'Concrete Poles',
      filterPo: isConcretePolesPurchaseOrder,
      sheetTitle: 'Concrete Poles Delivery Tracking',
      filePrefix: 'Concrete_Poles_Deliveries_All_POs',
      noDataMessage: 'No concrete poles purchase orders to export.',
      fetchRemoteMap: fetchConcretePolesDeliveriesStateMap,
      storageKey: CONCRETE_TABLES_STORAGE_KEY
    });

  const handleExportTransformersDeliveriesExcel = () =>
    exportDeliveriesTrackingExcel({
      requiredTab: 'transformers',
      tabLabel: 'Transformers',
      filterPo: isTransformerPurchaseOrder,
      sheetTitle: 'Transformer Delivery Tracking',
      filePrefix: 'Transformers_Deliveries_All_POs',
      noDataMessage: 'No transformer purchase orders to export.'
    });

  /**
   * Export the Poles Delivery table to Excel with one sheet per LOT.
   *
   * Only rows with at least one delivered quantity (> 0) are included.
   * Always emits a `LOT 8` and a `LOT 10` sheet (per requirement). Any rows
   * whose LOT column doesn't match either are collected into an extra
   * `Other LOTs` sheet so we never silently drop data.
   *
   * Each sheet mirrors the on-screen layout:
   *   - top metadata (LOT, row count, export date)
   *   - group-header row (Delivered Quantities / Delivery Details /
   *     Payments tracking) with merged cells
   *   - column-name row (Delivered "(D)" columns, DN No., DATE DELIVERED,
   *     SUPPLIER, PO No., Invoice No., Comment)
   *   - data rows, with `editableValues` overriding defaults
   *   - a Subtotal row at the bottom summing every (D) column
   *
   * Delivered cell values are converted to numbers when possible so
   * downstream Excel formulas (SUM, AVERAGE, etc.) work without manual
   * coercion. Date cells are formatted with the existing display helper.
   */
  const handleExportPolesDeliveriesExcel = () => {
    if (activeTable !== 'poles') {
      window.alert('Switch to the Poles tab, then click Export Excel.');
      return;
    }
    if (!polesData.length) {
      window.alert('No poles data to export.');
      return;
    }

    try {
      const lotIs8 = (lot) => /\blot[\s\-_]*8\b/i.test(String(lot || ''));
      const lotIs10 = (lot) => /\blot[\s\-_]*10\b/i.test(String(lot || ''));

      const rowHasDeliveredQty = (row) => {
        const rowKey = getPolesRowKey(row);
        return POLES_DELIVERED_BOQ_HEADERS.some((delHeader) => {
          const raw = editableValues?.[rowKey]?.[delHeader];
          if (raw === undefined || raw === null) return false;
          const str = String(raw).trim();
          if (!str || str === '-') return false;
          const num = Number(str.replace(/,/g, ''));
          return Number.isFinite(num) && num > 0;
        });
      };

      const lot8Rows = [];
      const lot10Rows = [];
      const otherRows = [];

      polesData.forEach((row) => {
        if (isBlankRow(row)) return;
        if (!rowHasDeliveredQty(row)) return;
        const rowLot = lotColumn ? String(row[lotColumn] || '').trim() : '';
        if (lotIs8(rowLot)) lot8Rows.push(row);
        else if (lotIs10(rowLot)) lot10Rows.push(row);
        else otherRows.push(row);
      });

      const totalDeliveredRows = lot8Rows.length + lot10Rows.length + otherRows.length;
      if (totalDeliveredRows === 0) {
        window.alert('No rows with delivered quantities to export.');
        return;
      }

      // Always include LOT 8 + LOT 10 so the workbook ships the two
      // requested sheets even when one of them is empty.
      const sheets = [
        { name: 'LOT 8', rows: lot8Rows },
        { name: 'LOT 10', rows: lot10Rows }
      ];
      if (otherRows.length > 0) {
        sheets.push({ name: 'Other LOTs', rows: otherRows });
      }

      // Convert raw strings/numbers to actual JS numbers when possible so
      // Excel treats BOQ/Delivered cells numerically.
      const toNumberOrText = (v) => {
        if (v === null || v === undefined) return '';
        const str = String(v).trim();
        if (str === '' || str === '-') return '';
        const cleaned = str.replace(/,/g, '');
        const num = Number(cleaned);
        return Number.isFinite(num) ? num : str;
      };

      const resolveCellForExport = (row, rowKey, header) => {
        const isDelivered = duplicateColumns.includes(header);
        const isDelivery = newColumns.includes(header);
        const isPayments = additionalColumns.includes(header);
        const isComment = header === commentColumn;

        if (isDelivered) {
          const v = editableValues[rowKey]?.[header];
          return toNumberOrText(v);
        }
        if (isDelivery || isPayments || isComment) {
          const v = editableValues[rowKey]?.[header];
          if (header === 'DATE DELIVERED') {
            return v ? (formatDateForPolesDisplay(v) || '') : '';
          }
          return v === undefined || v === null ? '' : String(v);
        }
        const raw = row[header];
        return raw === undefined || raw === null ? '' : raw;
      };

      const sumColumn = (rows, header) => {
        let sum = 0;
        rows.forEach((row) => {
          const rowKey = getPolesRowKey(row);
          const raw = editableValues[rowKey]?.[header];
          if (raw === null || raw === undefined || raw === '') return;
          const num = Number(String(raw).replace(/,/g, '').trim());
          if (Number.isFinite(num)) sum += num;
        });
        return sum;
      };

      const wb = XLSX.utils.book_new();
      const usedSheetNames = new Set();
      const dateLabel = new Date().toISOString().split('T')[0];

      sheets.forEach(({ name, rows }) => {
        const aoa = [];

        aoa.push(['Poles Delivery Tracking']);
        aoa.push(['LOT', name]);
        aoa.push(['Rows', rows.length]);
        aoa.push(['Exported', dateLabel]);
        aoa.push([]);

        // Group-header row (Delivered Quantities / Delivery Details /
        // Payments tracking) — merged across the matching column indices below.
        const merges = [];
        const groupRowExcelIdx = aoa.length;
        const groupRow = new Array(headersWithDuplicates.length).fill('');
        const pushGroup = (label, indices) => {
          if (!indices || indices.length === 0) return;
          const firstIdx = Math.min(...indices);
          const lastIdx = Math.max(...indices);
          groupRow[firstIdx] = label;
          if (lastIdx > firstIdx) {
            merges.push({
              s: { r: groupRowExcelIdx, c: firstIdx },
              e: { r: groupRowExcelIdx, c: lastIdx }
            });
          }
        };
        pushGroup('Delivered Quantities', duplicateColumnIndices);
        pushGroup('Delivery Details', newColumnIndices);
        pushGroup('Payments tracking', additionalColumnIndices);
        aoa.push(groupRow);

        // Column-name row.
        aoa.push([...headersWithDuplicates]);

        // Data rows.
        rows.forEach((row) => {
          const rowKey = getPolesRowKey(row);
          const line = headersWithDuplicates.map((header) =>
            resolveCellForExport(row, rowKey, header)
          );
          aoa.push(line);
        });

        // Subtotal row — only (D) columns get a number.
        const totalRow = new Array(headersWithDuplicates.length).fill('');
        totalRow[0] = 'Subtotal';
        duplicateColumns.forEach((col) => {
          const idx = headersWithDuplicates.indexOf(col);
          if (idx !== -1) totalRow[idx] = sumColumn(rows, col);
        });
        aoa.push(totalRow);

        const ws = XLSX.utils.aoa_to_sheet(aoa);
        if (merges.length > 0) ws['!merges'] = merges;
        // Give every column a sane default width so the sheet doesn't open
        // with all columns squished to the longest header.
        ws['!cols'] = headersWithDuplicates.map((header) => {
          if (header === commentColumn) return { wch: 28 };
          if (newColumns.includes(header) || additionalColumns.includes(header)) {
            return { wch: 18 };
          }
          if (duplicateColumns.includes(header)) {
            return { wch: 10 };
          }
          return { wch: 14 };
        });

        let sheetName = sanitizeExcelSheetName(name);
        let n = 1;
        while (usedSheetNames.has(sheetName)) {
          sheetName = sanitizeExcelSheetName(`${name}_${n}`);
          n += 1;
        }
        usedSheetNames.add(sheetName);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });

      XLSX.writeFile(wb, `Poles_Deliveries_${dateLabel}.xlsx`);
    } catch (err) {
      console.error(err);
      window.alert(err?.message || 'Failed to export poles deliveries.');
    }
  };

  // Format date to "5/Jan/2026" format (for poles table)
  const formatDateForPolesDisplay = (dateString) => {
    if (!dateString || dateString === '' || dateString === '-') return '';
    
    // If already in "5/Jan/2026" format, return as is
    if (dateString.includes('/') && dateString.length > 5) {
      return dateString;
    }
    
    // If in YYYY-MM-DD format (from date input), convert it
    if (dateString.includes('-') && dateString.length === 10) {
      try {
        const dateObj = new Date(dateString);
        const day = dateObj.getDate();
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[dateObj.getMonth()];
        const year = dateObj.getFullYear();
        return `${day}/${month}/${year}`;
      } catch (e) {
        return dateString;
      }
    }
    
    return dateString;
  };

  // Convert poles date strings to YYYY-MM-DD for <input type="date">
  const convertDateToInputFormat = (dateString) => {
    if (!dateString || dateString === '' || dateString === '-') return '';

    if (/^\d{4}-\d{2}-\d{2}$/.test(String(dateString).trim())) {
      return String(dateString).trim();
    }

    const slashOrHyphen = String(dateString).trim().match(
      /^(\d{1,2})[/-]([A-Za-z]{3})[/-](\d{2,4})$/i
    );
    if (slashOrHyphen) {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames.findIndex((m) => m.toLowerCase() === slashOrHyphen[2].toLowerCase()) + 1;
      const yearRaw = slashOrHyphen[3];
      const year = yearRaw.length === 2 ? `20${yearRaw}` : yearRaw;
      if (month > 0) {
        return `${year}-${String(month).padStart(2, '0')}-${String(slashOrHyphen[1]).padStart(2, '0')}`;
      }
    }

    try {
      const d = new Date(dateString);
      if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    } catch {
      /* ignore */
    }

    return '';
  };

  /** Stored poles date for one row (editableValues override). */
  const getPolesStoredDate = (rowKey, columnName, fallback = '') => {
    const raw = editableValues[rowKey]?.[columnName];
    if (raw !== undefined && raw !== '' && raw !== '-') return String(raw);
    if (fallback && fallback !== '-') return String(fallback);
    return '';
  };

  /**
   * Schedule a debounced Appwrite upsert for a single row's edits. We coalesce
   * keystrokes within `delayMs` so a user typing "1234" only fires one write,
   * not four. The snapshot is captured at fire-time so the very latest edit
   * map is what goes to the DB.
   */
  const schedulePolesUpsert = (rowKey, delayMs = 400) => {
    if (!rowKey || !polesEditsLoaded) return; // skip writes before the initial load completes
    const existingTimer = polesUpsertTimers.current[rowKey];
    if (existingTimer) clearTimeout(existingTimer);
    polesUpsertTimers.current[rowKey] = setTimeout(async () => {
      delete polesUpsertTimers.current[rowKey];
      const snapshot = polesUpsertSnapshots.current[rowKey] || {};
      try {
        await upsertPolesEdit(rowKey, snapshot);
      } catch (err) {
        // Non-fatal: leave the user's edits in local state so they keep
        // working. The next successful edit on the same row will retry.
        console.error('upsertPolesEdit failed for', rowKey, err);
      }
    }, delayMs);
  };

  // Handle input change for editable columns
  const handleEditableChange = (rowKey, columnName, value) => {
    if (!rowKey || isReadOnly) return;
    let valueToStore = value;

    // For DATE DELIVERED, convert YYYY-MM-DD to "5/Jan/2026" format
    if (columnName === 'DATE DELIVERED' && value) {
      valueToStore = formatDateForPolesDisplay(value);
    }
    
    setEditableValues(prev => {
      const nextRow = {
        ...(prev[rowKey] || {}),
        [columnName]: valueToStore
      };
      // Capture for the debounced upsert — read at fire-time inside
      // `schedulePolesUpsert` so the very latest edit always gets persisted.
      polesUpsertSnapshots.current[rowKey] = nextRow;
      return {
        ...prev,
        [rowKey]: nextRow
      };
    });
    schedulePolesUpsert(rowKey, columnName === 'DN No.' ? 200 : 400);
  };

  // Get the value for an editable column (formatted for display)
  const getEditableValue = (rowKey, columnName, defaultValue = '') => {
    const rawValue = editableValues[rowKey]?.[columnName];
    const inputKey = `${rowKey}-${columnName}`;
    const isFocused = focusedInput === inputKey;
    
    // For DATE DELIVERED column
    if (columnName === 'DATE DELIVERED') {
      if (rawValue !== undefined && rawValue !== '') {
        // When focused, show YYYY-MM-DD format for date input
        if (isFocused) {
          return convertDateToInputFormat(rawValue);
        }
        // When not focused, show formatted date
        return formatDateForPolesDisplay(rawValue);
      }
      // If no value in state, use default and convert if needed
      if (defaultValue && defaultValue !== '-') {
        if (isFocused) {
          return convertDateToInputFormat(defaultValue);
        }
        return formatDateForPolesDisplay(defaultValue);
      }
      return '';
    }
    
    // For other columns
    if (rawValue !== undefined && rawValue !== '') return rawValue;
    return defaultValue || '';
  };

  useEffect(() => {
    // Load Excel file
    const loadPolesData = async () => {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL}/assets/POLES.xlsx`);
        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        
        // Get the first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        // First row is headers, rest is data
        if (jsonData.length > 0) {
          const headers = jsonData[0];
          const rows = jsonData.slice(1)
            .map(row => {
            const rowObj = {};
            headers.forEach((header, index) => {
              rowObj[header] = row[index] || '';
            });
            return rowObj;
            })
            .filter(row => !isBlankRow(row));

          console.log('Total rows loaded:', rows.length);
          setPolesData(rows);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error loading poles data:', error);
        setLoading(false);
      }
    };

    loadPolesData();
  }, []);

  // Handle scroll to check if stats grid is visible
  useEffect(() => {
    let frame = 0;
    const handleScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        if (!statsGridRef.current) return;
        const statsRect = statsGridRef.current.getBoundingClientRect();
        const nextVisible = statsRect.bottom > 0;
        setIsStatsVisible((prev) => (prev === nextVisible ? prev : nextVisible));
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  // Load poles suppliers from suppliers list
  useEffect(() => {
    // Suppliers data (from Suppliers page - filtered by 'Wooden poles' category)
    const allSuppliers = [
      { id: 10, name: 'Poles T', category: 'Wooden poles' },
      { id: 9, name: 'Mufindi', category: 'Wooden poles' },
      { id: 8, name: 'Sunbird', category: 'Wooden poles' }
    ];
    
    // Filter suppliers with 'Wooden poles' category
    const polesSuppliersList = allSuppliers
      .filter(
        (supplier) =>
          supplier.category === 'Wooden poles' || supplier.category === 'Poles'
      )
      .map(supplier => supplier.name)
      .sort();
    
    setPolesSuppliers(polesSuppliersList);
  }, []);

  const polesSupplierFilterOptions = useMemo(() => {
    const fromMaster = polesSuppliers || [];
    const fromEdits = Object.values(editableValues || {})
      .map((row) => String(row?.SUPPLIER || '').trim())
      .filter(Boolean);
    return Array.from(new Set([...fromMaster, ...fromEdits])).sort((a, b) => a.localeCompare(b));
  }, [polesSuppliers, editableValues]);

  useEffect(() => {
    /** Single fetch → split into Concrete / Cables / Transformers / Meters PO arrays. */
    const loadMaterialDeliveryPos = async () => {
      setConcreteLoading(true);
      setCablesLoading(true);
      setTransformersLoading(true);
      setMetersLoading(true);
      try {
        const purchaseOrders = (await getAllPurchaseOrders()) || [];
        const concretePOs = purchaseOrders.filter(isConcretePolesPurchaseOrder);
        const polesPOs = purchaseOrders
          .filter(isWoodenPolesPurchaseOrder)
          .filter((po) => !concretePOs.some((c) => c.id === po.id));
        const cablesPOs = purchaseOrders
          .filter(isCablePurchaseOrder)
          .filter((po) => !concretePOs.some((c) => c.id === po.id));
        const transformerPOs = purchaseOrders
          .filter(isTransformerPurchaseOrder)
          .filter(
            (po) =>
              !cablesPOs.some((c) => c.id === po.id) &&
              !concretePOs.some((c) => c.id === po.id)
          );
        const meterPOs = purchaseOrders
          .filter(isMeterPurchaseOrder)
          .filter(
            (po) =>
              !cablesPOs.some((c) => c.id === po.id) &&
              !transformerPOs.some((t) => t.id === po.id) &&
              !concretePOs.some((c) => c.id === po.id)
          );
        setConcretePurchaseOrders(concretePOs);
        setPolesPurchaseOrders(polesPOs);
        setCablePurchaseOrders(cablesPOs);
        setTransformerPurchaseOrders(transformerPOs);
        setMeterPurchaseOrders(meterPOs);
      } catch (error) {
        console.error('Error loading delivery purchase orders:', error);
        setConcretePurchaseOrders([]);
        setPolesPurchaseOrders([]);
        setCablePurchaseOrders([]);
        setTransformerPurchaseOrders([]);
        setMeterPurchaseOrders([]);
      } finally {
        setConcreteLoading(false);
        setCablesLoading(false);
        setTransformersLoading(false);
        setMetersLoading(false);
      }
    };

    loadMaterialDeliveryPos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const cableLikePos = [
      ...cablePurchaseOrders,
      ...transformerPurchaseOrders,
      ...meterPurchaseOrders
    ];
    const concretePos = concretePurchaseOrders;
    if (!cableLikePos.length && !concretePos.length) return;
    let cancelled = false;
    cableDeliveriesHydratedRef.current = false;

    (async () => {
      let cableRemote = {};
      let concreteRemote = {};
      try {
        cableRemote = await fetchCableDeliveriesStateMap();
      } catch (err) {
        console.error('Failed to load cable deliveries from Appwrite:', err);
      }
      try {
        concreteRemote = await fetchConcretePolesDeliveriesStateMap();
      } catch (err) {
        console.error('Failed to load concrete poles deliveries from Appwrite:', err);
      }
      if (cancelled) return;

      let cableLocal = {};
      let concreteLocal = {};
      try {
        const raw = localStorage.getItem(CABLE_TABLES_STORAGE_KEY);
        if (raw) cableLocal = JSON.parse(raw) || {};
      } catch (_) {
        cableLocal = {};
      }
      try {
        const raw = localStorage.getItem(CONCRETE_TABLES_STORAGE_KEY);
        if (raw) concreteLocal = JSON.parse(raw) || {};
      } catch (_) {
        concreteLocal = {};
      }
      // Legacy: concrete grids previously lived in the cable local blob / cable collection
      if (!Object.keys(concreteLocal).length && cableLocal) {
        concretePos.forEach((po) => {
          if (cableLocal[po.id]) concreteLocal[po.id] = cableLocal[po.id];
        });
      }

      const applyGridForPo = (po, remote, local, next) => {
        const fromRemote = remote[po.id];
        const fromLocal = local[po.id];
        const hasRemoteGrid =
          fromRemote &&
          (fromRemote.deliveryDates?.length > 0 ||
            Object.keys(fromRemote.deliveryData || {}).length > 0);
        if (hasRemoteGrid) {
          next[po.id] = {
            deliveryDates: padCableDeliveryArray(
              fromRemote.deliveryDates?.length > 0 ? fromRemote.deliveryDates : EMPTY_CABLE_DELIVERY_SLOTS,
              EMPTY_CABLE_DELIVERY_SLOTS
            ),
            deliveryNumbers: padCableDeliveryArray(
              fromRemote.deliveryNumbers?.length > 0
                ? fromRemote.deliveryNumbers
                : EMPTY_CABLE_DELIVERY_SLOTS,
              EMPTY_CABLE_DELIVERY_SLOTS
            ),
            deliveryData: normalizeDeliveryDataCommas(
              fromRemote.deliveryData && Object.keys(fromRemote.deliveryData).length > 0
                ? fromRemote.deliveryData
                : buildInitialDeliveryDataForItems(normalizePoItemsForTable(po))
            )
          };
        } else if (fromLocal) {
          next[po.id] = {
            deliveryDates: padCableDeliveryArray(fromLocal.deliveryDates, EMPTY_CABLE_DELIVERY_SLOTS),
            deliveryNumbers: padCableDeliveryArray(fromLocal.deliveryNumbers, EMPTY_CABLE_DELIVERY_SLOTS),
            deliveryData: normalizeDeliveryDataCommas(
              fromLocal.deliveryData && Object.keys(fromLocal.deliveryData).length > 0
                ? fromLocal.deliveryData
                : buildInitialDeliveryDataForItems(normalizePoItemsForTable(po))
            )
          };
        } else if (!next[po.id]) {
          next[po.id] = {
            deliveryDates: [...EMPTY_CABLE_DELIVERY_SLOTS],
            deliveryNumbers: [...EMPTY_CABLE_DELIVERY_SLOTS],
            deliveryData: buildInitialDeliveryDataForItems(normalizePoItemsForTable(po))
          };
        }
      };

      cableDeliveriesHydratedRef.current = true;
      setCableTablesState((prev) => {
        const next = { ...prev };
        concretePos.forEach((po) => {
          // Prefer dedicated concrete collection; fall back to old cable collection rows
          const mergedRemote = {
            ...cableRemote,
            ...concreteRemote
          };
          // If concrete has an entry for this PO, it already wins via spread order
          applyGridForPo(po, mergedRemote, concreteLocal, next);
        });
        cableLikePos.forEach((po) => applyGridForPo(po, cableRemote, cableLocal, next));
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [concretePurchaseOrders, cablePurchaseOrders, transformerPurchaseOrders, meterPurchaseOrders]);

  useEffect(() => {
    if (!cableDeliveriesHydratedRef.current) return;
    if (cableSaveDebounceRef.current) clearTimeout(cableSaveDebounceRef.current);
    cableSaveDebounceRef.current = setTimeout(async () => {
      const concreteIds = new Set(concretePurchaseOrders.map((p) => p.id));
      const cableIds = new Set([
        ...cablePurchaseOrders.map((p) => p.id),
        ...transformerPurchaseOrders.map((p) => p.id),
        ...meterPurchaseOrders.map((p) => p.id)
      ]);

      const concreteSlice = {};
      const cableSlice = {};
      for (const [poId, state] of Object.entries(cableTablesState)) {
        if (concreteIds.has(poId)) concreteSlice[poId] = state;
        else if (cableIds.has(poId)) cableSlice[poId] = state;
      }

      try {
        localStorage.setItem(CABLE_TABLES_STORAGE_KEY, JSON.stringify(cableSlice));
      } catch (err) {
        console.error('Failed to save cable delivery backup locally:', err);
      }
      try {
        localStorage.setItem(CONCRETE_TABLES_STORAGE_KEY, JSON.stringify(concreteSlice));
      } catch (err) {
        console.error('Failed to save concrete poles delivery backup locally:', err);
      }

      for (const poId of Object.keys(concreteSlice)) {
        try {
          await upsertConcretePolesDelivery(poId, concreteSlice[poId]);
        } catch (err) {
          console.error('Failed to sync concrete poles delivery to Appwrite:', err);
        }
      }
      for (const poId of Object.keys(cableSlice)) {
        try {
          await upsertCableDelivery(poId, cableSlice[poId]);
        } catch (err) {
          console.error('Failed to sync delivery to Appwrite:', err);
        }
      }
    }, 400);
    return () => {
      if (cableSaveDebounceRef.current) clearTimeout(cableSaveDebounceRef.current);
    };
    // normalizePoItemsForTable is stable for this debounced persistence/sync pass.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cableTablesState, concretePurchaseOrders, cablePurchaseOrders, transformerPurchaseOrders, meterPurchaseOrders]);

  useEffect(() => {
    const poId = activeDeliveryEditor?.poId || activeDeliveryNumberEditor?.poId;
    if (!poId) return;
    setExpandedMaterialPoIds((prev) => {
      if (prev.has(poId)) return prev;
      const next = new Set(prev);
      next.add(poId);
      return next;
    });
  }, [activeDeliveryEditor, activeDeliveryNumberEditor]);

  // Find LOT column (exact "Lot" header — not "Total poles")
  const lotColumn = getPolesLotColumn(polesData);
  polesDataRef.current = polesData;
  lotColumnRef.current = lotColumn || 'Lot';

  // Get unique LOT values
  const getUniqueLots = () => {
    if (!lotColumn || polesData.length === 0) return [];
    const lots = polesData
      .map(row => row[lotColumn])
      .filter(lot => lot && String(lot).trim() !== '')
      .map(lot => String(lot).trim());
    return [...new Set(lots)].sort();
  };

  const uniqueLots = getUniqueLots();
  const polesLotTabs = sortPolesLotTabs(uniqueLots);
  const preferredLotTab = preferLot10
    ? polesLotTabs.find((lot) => /\blot[\s\-_]*10\b/i.test(lot)) || polesLotTabs[0] || ''
    : polesLotTabs.find((lot) => /\blot[\s\-_]*8\b/i.test(lot)) || polesLotTabs[0] || '';
  const effectivePolesLotTab =
    polesLotTab &&
    polesLotTabs.includes(polesLotTab) &&
    (!preferLot10 || /\blot[\s\-_]*10\b/i.test(polesLotTab))
      ? polesLotTab
      : preferredLotTab;
  const visiblePolesLotTabs = isLot10Locked
    ? polesLotTabs.filter((lot) => /\blot[\s\-_]*10\b/i.test(lot))
    : polesLotTabs;

  const visibleMaterialLotTabs = isLot10Locked
    ? MATERIAL_LOT_TABS.filter((lot) => /\blot[\s\-_]*10\b/i.test(lot))
    : MATERIAL_LOT_TABS;
  const preferredMaterialLotTab = preferLot10 ? 'LOT 10' : 'LOT 8';
  const effectiveMaterialLotTab =
    materialLotTab && visibleMaterialLotTabs.includes(materialLotTab)
      ? materialLotTab
      : visibleMaterialLotTabs.includes(preferredMaterialLotTab)
        ? preferredMaterialLotTab
        : visibleMaterialLotTabs[0] || 'LOT 8';

  useEffect(() => {
    if (!MATERIAL_DELIVERY_TABS.includes(activeTable)) {
      setMaterialPanel('delivery');
      return;
    }
    if (!visibleMaterialLotTabs.includes(materialLotTab)) {
      setMaterialLotTab(effectiveMaterialLotTab);
    }
  }, [activeTable, materialLotTab, visibleMaterialLotTabs, effectiveMaterialLotTab]);

  useEffect(() => {
    if (!isPolesLot10Only) return;
    if (activeTable !== 'poles') setActiveTable('poles');
    if (polesPanel !== 'delivery') setPolesPanel('delivery');
  }, [isPolesLot10Only, activeTable, polesPanel]);

  useEffect(() => {
    if (activeTable !== 'poles' || polesLotTabs.length === 0) return;
    if (!polesLotTab || !polesLotTabs.includes(polesLotTab) || (preferLot10 && !/\blot[\s\-_]*10\b/i.test(polesLotTab))) {
      setPolesLotTab(effectivePolesLotTab);
    }
  }, [activeTable, polesLotTab, polesLotTabs, effectivePolesLotTab, preferLot10]);

  // Filter poles data based on search query and active LOT tab
  const filteredPolesData = polesData.filter((row) => {
    // First, remove blank rows
    if (isBlankRow(row)) {
      return false;
    }

    // One LOT per view — same switch pattern as Material Inventory
    if (effectivePolesLotTab && lotColumn) {
      if (!rowMatchesPolesLotTab(row, lotColumn, effectivePolesLotTab)) {
        return false;
      }
    }

    // Optional filter: only show rows where at least one Delivered Quantities
    // (the "(D)" columns) has a filled value saved in editableValues.
    if (activeTable === 'poles' && onlyDeliveredFilled && polesEditsLoaded) {
      const rowKey = getPolesRowKey(row);
      const hasAnyDelivered = POLES_DELIVERED_BOQ_HEADERS.some((delHeader) => {
        const raw = editableValues?.[rowKey]?.[delHeader];
        if (raw === undefined || raw === null) return false;
        const str = String(raw).trim();
        if (!str || str === '-' || str === '') return false;
        const num = Number(str.replace(/,/g, ''));
        // Treat numeric > 0 as "filled".
        // (If you want "any non-empty string" we can adjust.)
        return Number.isFinite(num) && num > 0;
      });
      if (!hasAnyDelivered) return false;
    }

    // Optional filter: supplier selected from Delivery Details / SUPPLIER field.
    if (activeTable === 'poles' && supplierFilter !== 'all') {
      const rowKey = getPolesRowKey(row);
      const supplier = String(editableValues?.[rowKey]?.SUPPLIER || '').trim();
      if (supplier !== supplierFilter) return false;
    }

    // Filter by search query (source row + editable fields like DN No., Supplier, etc.)
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const rowKey = getPolesRowKey(row);
    const editableRow = editableValues?.[rowKey] || {};
    const sourceMatch = Object.values(row).some((value) =>
      String(value).toLowerCase().includes(query)
    );
    if (sourceMatch) return true;
    return Object.values(editableRow).some((value) =>
      String(value).toLowerCase().includes(query)
    );
  });

  // Get column headers from the first row of data
  const getHeaders = () => {
    if (polesData.length === 0) return [];
    return Object.keys(polesData[0]);
  };

  const allHeaders = getHeaders();

  // Filter out the BOQ column, total poles column, empty headers, and first column
  const baseHeaders = allHeaders.filter((header, index) => {
    // Exclude the first column (index 0)
    if (index === 0) return false;
    const headerLower = String(header).toLowerCase().trim();
    if (!headerLower) return false;
    // Exclude the BOQ column (exact match, case-insensitive)
    if (headerLower === 'boq') return false;
    // Exclude columns with "total" and "poles" in the name
    if (headerLower.includes('total') && headerLower.includes('poles')) return false;
    return true;
  });

  // Reconcile against POLES_BOQ_DEFINITIONS so every canonical pole type exists
  // for Delivered Quantities labels (even when POLES.xlsx is missing a column).
  const headersWithSurvey = reconcilePolesBoqHeaders(baseHeaders);
  // eslint-disable-next-line no-unused-vars
  const boqColumns = headersWithSurvey.filter(isPolesBoqHeader);
  // Survey Quantities are removed from the UI — keep identity + non-BOQ only.
  const headers = headersWithSurvey.filter((h) => !isPolesBoqHeader(h) && String(h || '').trim());

  // Insert Delivered Quantities after Hamlet info columns (canonical “(D)” names).
  const headersWithDuplicates = [...headers];
  const deliveredInsertIndex = Math.min(POLES_HAMLET_INFO_COL_COUNT, headersWithDuplicates.length);
  const duplicateColumns = POLES_DELIVERED_BOQ_HEADERS;
  if (duplicateColumns.length > 0) {
    duplicateColumns.forEach((col, idx) => {
      headersWithDuplicates.splice(deliveredInsertIndex + idx, 0, col);
    });
  }

  // Delivered column indices (for header spanning / editable cells)
  const duplicateColumnIndices = duplicateColumns
    .map((col) => headersWithDuplicates.indexOf(col))
    .filter((idx) => idx >= 0);
  const allBOQColumnIndices = duplicateColumnIndices;
  const firstDeliveredIndex =
    duplicateColumnIndices.length > 0 ? Math.min(...duplicateColumnIndices) : -1;

  // Find the index after the last delivered column to insert new columns
  const getLastDeliveredIndex = () => {
    if (duplicateColumnIndices.length > 0) {
      return Math.max(...duplicateColumnIndices) + 1;
    }
    return deliveredInsertIndex;
  };
  
  const lastDeliveredIndex = getLastDeliveredIndex();
  
  // Add the new columns: DN No., DATE DELIVERED, SUPPLIER, PO No., Invoice No.
  const newColumns = ['DN No.', 'DATE DELIVERED', 'SUPPLIER'];
  newColumns.forEach((col, idx) => {
    headersWithDuplicates.splice(lastDeliveredIndex + idx, 0, col);
  });
  
  // Get indices of the new columns for header spanning
  const newColumnIndices = newColumns
    .map((col) => headersWithDuplicates.indexOf(col))
    .filter((idx) => idx >= 0);
  
  // Add PO No. and Invoice No. columns after the Delivery Details columns
  const additionalColumns = ['PO No.', 'Invoice No.'];
  const lastNewColumnIndex =
    newColumnIndices.length > 0
      ? Math.max(...newColumnIndices) + 1
      : headersWithDuplicates.length;
  additionalColumns.forEach((col, idx) => {
    headersWithDuplicates.splice(lastNewColumnIndex + idx, 0, col);
  });

  // Get indices of additional columns
  const additionalColumnIndices = additionalColumns
    .map((col) => headersWithDuplicates.indexOf(col))
    .filter((idx) => idx >= 0);

  // Comment column after Invoice No.
  const commentColumn = 'Comment';
  const commentColumnIndex =
    additionalColumnIndices.length > 0
      ? Math.max(...additionalColumnIndices) + 1
      : headersWithDuplicates.length;
  headersWithDuplicates.splice(commentColumnIndex, 0, commentColumn);

  // Get the last 8 column indices (Delivered columns + Delivery Details + Payments tracking)
  const getLast8ColumnIndices = () => {
    const last8Indices = [];
    // Add duplicate column indices (Delivered columns)
    last8Indices.push(...duplicateColumnIndices);
    // Add new column indices (Delivery Details)
    last8Indices.push(...newColumnIndices);
    // Add additional column indices (Payments tracking)
    last8Indices.push(...additionalColumnIndices);
    // Comment column
    last8Indices.push(commentColumnIndex);
    // Sort to get them in order
    return last8Indices.sort((a, b) => a - b);
  };

  const last8ColumnIndices = getLast8ColumnIndices();

  // Get editable column names from the last 8 columns
  const getEditableColumnNames = () => {
    return last8ColumnIndices.map(colIndex => headersWithDuplicates[colIndex]);
  };

  // eslint-disable-next-line no-unused-vars
  const editableColumnNames = getEditableColumnNames();

  /**
   * Load persisted Poles edits from Appwrite once on mount and merge them
   * into `editableValues`. Editable cells (Delivered Quantities, DN/Date/Supplier,
   * PO/Invoice, Comment) start blank unless they have a saved value in
   * `PolesDeliveriesSGM`. Survey Quantity keys are stripped on load/save.
   *
   * Runs after `polesData` has loaded so a row's `rowKey` can be matched
   * against the Excel-derived rows by Hamlet identity — that way the merge
   * is keyed by `rowKey`, not by spreadsheet index.
   *
   * If the Appwrite collection hasn't been created yet (404) or CORS blocks
   * the fetch, we log and carry on with empty state — the page stays
   * fully usable, edits just won't persist until setup is run.
   */
  useEffect(() => {
    let cancelled = false;
    const loadPersistedEdits = async () => {
      try {
        const map = await fetchPolesEditsMap();
        if (cancelled) return;
        setEditableValues((prev) => {
          // Merge persisted edits underneath any in-memory edits the user
          // already made before the fetch landed (unlikely, but safe).
          const next = { ...map };
          Object.keys(prev).forEach((key) => {
            next[key] = { ...(next[key] || {}), ...prev[key] };
          });
          return next;
        });
        // Seed the per-row snapshot ref so the very first edit upsert sends
        // the merged row (not just the typed cell on its own).
        Object.keys(map).forEach((key) => {
          polesUpsertSnapshots.current[key] = { ...map[key] };
        });
      } catch (err) {
        console.error('Failed to load Poles edits from Appwrite:', err);
      } finally {
        if (!cancelled) setPolesEditsLoaded(true);
      }
    };
    loadPersistedEdits();
    return () => {
      cancelled = true;
    };
  }, []);

  // Hamlet (= row) counts. Kept for the table subtitle and filter pill.
  const totalPoles = polesData.length;
  // eslint-disable-next-line no-unused-vars
  const filteredPolesCount = filteredPolesData.length;

  const lotPolesCount = useMemo(() => {
    if (!polesLotTab || !lotColumn) return totalPoles;
    return polesData.filter(
      (row) => !isBlankRow(row) && rowMatchesPolesLotTab(row, lotColumn, polesLotTab)
    ).length;
  }, [polesData, polesLotTab, lotColumn, totalPoles]);

  // Check if filters are active
  const hasActiveFilters =
    searchQuery.trim() !== '' ||
    (activeTable === 'poles' && (onlyDeliveredFilled || supplierFilter !== 'all'));

  /** Sum of delivered (D) quantities and hamlets with at least one delivery. */
  const polesDeliveredStats = useMemo(() => {
    const parseDelivered = (rawValue) => {
      if (rawValue === null || rawValue === undefined || rawValue === '') return 0;
      const cleaned = String(rawValue).replace(/,/g, '').trim();
      if (!cleaned || cleaned === '-') return 0;
      const num = Number(cleaned);
      return Number.isFinite(num) ? num : 0;
    };
    const tally = (rows) => {
      let polesDelivered = 0;
      let hamletsDelivered = 0;
      rows.forEach((row) => {
        if (isBlankRow(row)) return;
        const rowKey = getPolesRowKey(row);
        let rowDelivered = 0;
        duplicateColumns.forEach((col) => {
          rowDelivered += parseDelivered(editableValues?.[rowKey]?.[col]);
        });
        polesDelivered += rowDelivered;
        if (rowDelivered > 0) hamletsDelivered += 1;
      });
      return { polesDelivered, hamletsDelivered };
    };
    const lotRows =
      polesLotTab && lotColumn
        ? polesData.filter(
            (row) => !isBlankRow(row) && rowMatchesPolesLotTab(row, lotColumn, polesLotTab)
          )
        : polesData.filter((row) => !isBlankRow(row));
    return {
      all: tally(lotRows),
      filtered: tally(filteredPolesData)
    };
  }, [polesData, filteredPolesData, duplicateColumns, editableValues, polesLotTab, lotColumn]);

  // Calculate subtotals for Delivered Quantities columns (D columns).
  const calculateDeliveredSubtotals = () => {
    const deliveredSubtotals = {};
    duplicateColumns.forEach((col) => {
      let sum = 0;
      filteredPolesData.forEach((row) => {
        const rowKey = getPolesRowKey(row);
        const edited = editableValues[rowKey]?.[col];
        if (edited === null || edited === undefined || edited === '') return;
        const cleaned = String(edited).replace(/,/g, '').trim();
        const numValue = Number(cleaned);
        if (!Number.isNaN(numValue)) {
          sum += numValue;
        }
      });
      deliveredSubtotals[col] = sum;
    });
    return deliveredSubtotals;
  };

  const deliveredSubtotals = calculateDeliveredSubtotals();

  const supplierDeliveredSummary = useMemo(() => {
    if (activeTable !== 'poles' || supplierFilter === 'all') return null;

    const byType = {};
    duplicateColumns.forEach((col) => {
      const label = col.replace(' (D)', '');
      byType[label] = 0;
    });

    filteredPolesData.forEach((row) => {
      const rowKey = getPolesRowKey(row);
      duplicateColumns.forEach((col) => {
        const raw = editableValues?.[rowKey]?.[col];
        if (raw === null || raw === undefined || raw === '' || raw === '-') return;
        const num = Number(String(raw).replace(/,/g, '').trim());
        if (!Number.isNaN(num) && Number.isFinite(num)) {
          byType[col.replace(' (D)', '')] += num;
        }
      });
    });

    const entries = Object.entries(byType)
      .filter(([, qty]) => qty > 0)
      .sort((a, b) => b[1] - a[1]);

    return {
      supplier: supplierFilter,
      entries
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTable, supplierFilter, duplicateColumns, filteredPolesData, headers, editableValues]);

  const renderPolesSubtotalsContent = () => (
    <>
      <span className="poles-subtotals-label poles-subtotals-label--delivered">Subtotals — Delivered:</span>
      {duplicateColumns.map((col) => (
        <span key={`sub-del-${col}`} className="poles-subtotals-item poles-subtotals-item--delivered">
          <span className="poles-subtotals-col">{col.replace(' (D)', '')}</span>
          <span className="subtotal-cell poles-subtotals-val">{(deliveredSubtotals[col] || 0).toLocaleString()}</span>
        </span>
      ))}
    </>
  );

  /**
   * Aggregate totals across every cable PO: sum of PO Qty (applying the ×1000 rule
   * for the special PO numbers) and sum of delivered qty pulled from each PO's saved
   * delivery table state. The average % delivered is (totalDelivered / totalPoQty) × 100.
   */
  const cablesTotals = useMemo(() => {
    let totalPoQty = 0;
    let totalDelivered = 0;

    (cablePurchaseOrders || []).forEach((po) => {
      const items = normalizePoItemsForTable(po);
      const tableState = cableTablesState[po.id];
      const deliveryData = tableState?.deliveryData || {};

      items.forEach((item, rowIndex) => {
        const poQtyNum = getPoQtyNumeric(po, item?.qty, item?.itemName);
        if (poQtyNum !== null && !Number.isNaN(poQtyNum)) {
          totalPoQty += poQtyNum;
        }
        const rowDelivered = calculateRowTotal(rowIndex, deliveryData);
        if (!Number.isNaN(rowDelivered)) {
          totalDelivered += rowDelivered;
        }
      });
    });

    const percent = totalPoQty > 0
      ? Math.min(100, (totalDelivered / totalPoQty) * 100)
      : 0;

    return { totalPoQty, totalDelivered, percent };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cablePurchaseOrders, cableTablesState]);

  const formatCableQty = (n) =>
    Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

  /** Shared helper: sum delivered + PO qty across every line of every PO in `pos`. */
  const aggregatePoQtyAndDelivered = (pos) => {
    let totalPoQty = 0;
    let totalDelivered = 0;
    (pos || []).forEach((po) => {
      const items = normalizePoItemsForTable(po);
      const tableState = cableTablesState[po.id];
      const deliveryDataForPo = tableState?.deliveryData || {};
      items.forEach((item, rowIndex) => {
        const poQtyNum = getPoQtyNumeric(po, item?.qty, item?.itemName);
        if (poQtyNum !== null && !Number.isNaN(poQtyNum)) totalPoQty += poQtyNum;
        const rowDelivered = calculateRowTotal(rowIndex, deliveryDataForPo);
        if (!Number.isNaN(rowDelivered)) totalDelivered += rowDelivered;
      });
    });
    const percent = totalPoQty > 0
      ? Math.min(100, (totalDelivered / totalPoQty) * 100)
      : 0;
    return { totalPoQty, totalDelivered, percent };
  };

  /** Transformer totals: sum of all transformer-PO line items + their delivered state. */
  const transformersTotals = useMemo(
    () => aggregatePoQtyAndDelivered(transformerPurchaseOrders),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [transformerPurchaseOrders, cableTablesState]
  );

  /** Meter totals: same as transformers, sourced from meter POs. */
  const metersTotals = useMemo(
    () => aggregatePoQtyAndDelivered(meterPurchaseOrders),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [meterPurchaseOrders, cableTablesState]
  );

  /** Concrete poles totals from concrete POs (same delivery grid as cables). */
  const concreteTotals = useMemo(
    () => aggregatePoQtyAndDelivered(concretePurchaseOrders),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [concretePurchaseOrders, cableTablesState]
  );

  return (
    <div className="dt-page">
      <div className="dt-scroll">
        <div className="dt-panel">
          <AppBar
            variant="inset"
            breadcrumbs={
              isPolesLot10Only
                ? [
                    { label: 'Platform', hideOnMobile: true },
                    { label: 'Sagemcom', to: '/sagemcom' },
                    { label: 'Logistics', to: '/sagemcom/logistics', hideOnMobile: true },
                    { label: 'Poles LOT 10' },
                  ]
                : [
                    { label: 'Platform', hideOnMobile: true },
                    { label: 'Sagemcom', to: '/sagemcom' },
                    { label: 'Logistics', to: '/sagemcom/logistics', hideOnMobile: true },
                    { label: 'Delivery tracking' },
                  ]
            }
          />

          <section className="dt-intro" aria-label="Delivery tracking overview">
            <div className="dt-intro-text">
              <h1 className="dt-intro-title">
                {isPolesLot10Only ? 'Poles tracking — LOT 10' : 'Delivery tracking'}
              </h1>
              <p className="dt-intro-subtitle">
                {isPolesLot10Only
                  ? 'LOT 10 poles delivery and trucking data'
                  : 'Track poles, cables, transformers, and meters against purchase orders'}
              </p>
            </div>
          </section>

      {!isPolesLot10Only && (
      <div className="dt-categories-grid" ref={statsGridRef}>
        <button
          type="button"
          className={`dt-category-card${activeTable === 'poles' ? ' is-selected' : ''}`}
          onClick={() => setActiveTable('poles')}
          style={{ '--cat-accent': '#185FA5' }}
          aria-pressed={activeTable === 'poles'}
        >
          <div className="dt-category-card__top">
            <span className="dt-category-card__icon" aria-hidden>
              <TreePine className="h-4 w-4" />
            </span>
            <span className="dt-category-card__label">Poles</span>
            {activeTable === 'poles' ? (
              <span className="dt-category-card__check" aria-hidden>
                <Check className="h-3.5 w-3.5" />
              </span>
            ) : null}
          </div>
          <div className="dt-category-card__count">
            {loading
              ? 'Loading…'
              : hasActiveFilters
                ? `${polesDeliveredStats.filtered.hamletsDelivered.toLocaleString()} / ${polesDeliveredStats.all.hamletsDelivered.toLocaleString()} hamlets`
                : `${polesDeliveredStats.all.hamletsDelivered.toLocaleString()} / ${lotPolesCount.toLocaleString()} hamlets`}
          </div>
          <div
            className="dt-category-card__amount"
            title="Total poles delivered (sum of all Delivered Qty values you've entered)."
          >
            {loading
              ? '—'
              : (hasActiveFilters
                  ? polesDeliveredStats.filtered.polesDelivered.toLocaleString()
                  : polesDeliveredStats.all.polesDelivered.toLocaleString())}
          </div>
          <div className="dt-category-card__meta">
            <div>
              <span>Delivered qty</span>
              <strong>
                {loading
                  ? '—'
                  : hasActiveFilters
                    ? `${polesDeliveredStats.filtered.polesDelivered.toLocaleString()} / ${polesDeliveredStats.all.polesDelivered.toLocaleString()}`
                    : polesDeliveredStats.all.polesDelivered.toLocaleString()}
              </strong>
            </div>
            <div>
              <span>Hamlets</span>
              <strong>
                {loading
                  ? '—'
                  : hasActiveFilters
                    ? `${polesDeliveredStats.filtered.hamletsDelivered.toLocaleString()} / ${lotPolesCount.toLocaleString()}`
                    : `${polesDeliveredStats.all.hamletsDelivered.toLocaleString()} / ${lotPolesCount.toLocaleString()}`}
              </strong>
            </div>
          </div>
        </button>

        <button
          type="button"
          className={`dt-category-card${activeTable === 'concrete-poles' ? ' is-selected' : ''}`}
          onClick={() => setActiveTable('concrete-poles')}
          style={{ '--cat-accent': '#5B7C99' }}
          aria-pressed={activeTable === 'concrete-poles'}
        >
          <div className="dt-category-card__top">
            <span className="dt-category-card__icon" aria-hidden>
              <Columns3 className="h-4 w-4" />
            </span>
            <span className="dt-category-card__label">Concrete poles</span>
            {activeTable === 'concrete-poles' ? (
              <span className="dt-category-card__check" aria-hidden>
                <Check className="h-3.5 w-3.5" />
              </span>
            ) : null}
          </div>
          <div className="dt-category-card__count">
            {concreteLoading ? 'Loading…' : 'Avg delivered across POs'}
          </div>
          <div
            className="dt-category-card__amount"
            title="Average % delivered across all concrete poles POs (sum of delivered ÷ sum of PO Qty)"
          >
            {concreteLoading ? '—' : `${concreteTotals.percent.toFixed(1)}%`}
          </div>
          <div className="dt-category-card__meta">
            <div>
              <span>Delivered</span>
              <strong>{concreteLoading ? '—' : formatCableQty(concreteTotals.totalDelivered)}</strong>
            </div>
            <div>
              <span>PO qty</span>
              <strong>{concreteLoading ? '—' : formatCableQty(concreteTotals.totalPoQty)}</strong>
            </div>
          </div>
        </button>

        <button
          type="button"
          className={`dt-category-card${activeTable === 'cables' ? ' is-selected' : ''}`}
          onClick={() => setActiveTable('cables')}
          style={{ '--cat-accent': '#639922' }}
          aria-pressed={activeTable === 'cables'}
        >
          <div className="dt-category-card__top">
            <span className="dt-category-card__icon" aria-hidden>
              <Cable className="h-4 w-4" />
            </span>
            <span className="dt-category-card__label">Cables</span>
            {activeTable === 'cables' ? (
              <span className="dt-category-card__check" aria-hidden>
                <Check className="h-3.5 w-3.5" />
              </span>
            ) : null}
          </div>
          <div className="dt-category-card__count">
            {cablesLoading ? 'Loading…' : 'Avg delivered across POs'}
          </div>
          <div
            className="dt-category-card__amount"
            title="Average % delivered across all cable POs (sum of delivered ÷ sum of PO Qty)"
          >
            {cablesLoading ? '—' : `${cablesTotals.percent.toFixed(1)}%`}
          </div>
          <div className="dt-category-card__meta">
            <div>
              <span>Delivered</span>
              <strong>{cablesLoading ? '—' : formatCableQty(cablesTotals.totalDelivered)}</strong>
            </div>
            <div>
              <span>PO qty</span>
              <strong>{cablesLoading ? '—' : formatCableQty(cablesTotals.totalPoQty)}</strong>
            </div>
          </div>
        </button>

        <button
          type="button"
          className={`dt-category-card${activeTable === 'transformers' ? ' is-selected' : ''}`}
          onClick={() => setActiveTable('transformers')}
          style={{ '--cat-accent': '#BA7517' }}
          aria-pressed={activeTable === 'transformers'}
        >
          <div className="dt-category-card__top">
            <span className="dt-category-card__icon" aria-hidden>
              <Zap className="h-4 w-4" />
            </span>
            <span className="dt-category-card__label">Transformers</span>
            {activeTable === 'transformers' ? (
              <span className="dt-category-card__check" aria-hidden>
                <Check className="h-3.5 w-3.5" />
              </span>
            ) : null}
          </div>
          <div className="dt-category-card__count">
            {transformersLoading ? 'Loading…' : 'Avg delivered across POs'}
          </div>
          <div
            className="dt-category-card__amount"
            title="Average % delivered across all transformer POs (sum of delivered ÷ sum of PO Qty)"
          >
            {transformersLoading ? '—' : `${transformersTotals.percent.toFixed(1)}%`}
          </div>
          <div className="dt-category-card__meta">
            <div>
              <span>Delivered</span>
              <strong>{transformersLoading ? '—' : formatCableQty(transformersTotals.totalDelivered)}</strong>
            </div>
            <div>
              <span>PO qty</span>
              <strong>{transformersLoading ? '—' : formatCableQty(transformersTotals.totalPoQty)}</strong>
            </div>
          </div>
        </button>

        <button
          type="button"
          className={`dt-category-card${activeTable === 'meters' ? ' is-selected' : ''}`}
          onClick={() => setActiveTable('meters')}
          style={{ '--cat-accent': '#7F77DD' }}
          aria-pressed={activeTable === 'meters'}
        >
          <div className="dt-category-card__top">
            <span className="dt-category-card__icon" aria-hidden>
              <Gauge className="h-4 w-4" />
            </span>
            <span className="dt-category-card__label">Meters</span>
            {activeTable === 'meters' ? (
              <span className="dt-category-card__check" aria-hidden>
                <Check className="h-3.5 w-3.5" />
              </span>
            ) : null}
          </div>
          <div className="dt-category-card__count">
            {metersLoading ? 'Loading…' : 'Avg delivered across POs'}
          </div>
          <div
            className="dt-category-card__amount"
            title="Average % delivered across all meter POs (sum of delivered ÷ sum of PO Qty)"
          >
            {metersLoading ? '—' : `${metersTotals.percent.toFixed(1)}%`}
          </div>
          <div className="dt-category-card__meta">
            <div>
              <span>Delivered</span>
              <strong>{metersLoading ? '—' : formatCableQty(metersTotals.totalDelivered)}</strong>
            </div>
            <div>
              <span>PO qty</span>
              <strong>{metersLoading ? '—' : formatCableQty(metersTotals.totalPoQty)}</strong>
            </div>
          </div>
        </button>
      </div>
      )}

      <div className="delivery-content">
          <div className="dt-controls-chrome">
          {activeTable === 'poles' && !isPolesLot10Only && !loading && (
            <div className="dt-lot-tabs" role="tablist" aria-label="Poles LOT">
              {(visiblePolesLotTabs.length > 0 ? visiblePolesLotTabs : polesLotTabs).map((lot) => (
                <button
                  key={lot}
                  type="button"
                  role="tab"
                  className={`dt-lot-tabs__btn${
                    polesPanel === 'delivery' && polesLotTab === lot ? ' is-active' : ''
                  }`}
                  onClick={() => {
                    setPolesLotTab(lot);
                    setPolesPanel('delivery');
                  }}
                >
                  {lot}
                </button>
              ))}
              {!isLot10Locked ? (
              <button
                type="button"
                role="tab"
                className={`dt-lot-tabs__btn${
                  polesPanel === 'po-quantities' ? ' is-active' : ''
                }`}
                onClick={() => setPolesPanel('po-quantities')}
              >
                PO Quantities
              </button>
              ) : null}
            </div>
          )}

          {MATERIAL_DELIVERY_TABS.includes(activeTable) && !isPolesLot10Only ? (
            <div className="dt-lot-tabs" role="tablist" aria-label="Material LOT">
              {visibleMaterialLotTabs.map((lot) => (
                <button
                  key={`material-lot-${lot}`}
                  type="button"
                  role="tab"
                  className={`dt-lot-tabs__btn${
                    materialPanel === 'delivery' && materialLotTab === lot ? ' is-active' : ''
                  }`}
                  onClick={() => {
                    setMaterialLotTab(lot);
                    setMaterialPanel('delivery');
                  }}
                >
                  {lot}
                </button>
              ))}
              {!isLot10Locked ? (
                <button
                  type="button"
                  role="tab"
                  className={`dt-lot-tabs__btn${
                    materialPanel === 'po-quantities' ? ' is-active' : ''
                  }`}
                  onClick={() => setMaterialPanel('po-quantities')}
                >
                  PO Quantities
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="dt-toolbar">
            {!(
              (activeTable === 'poles' && polesPanel === 'po-quantities') ||
              (MATERIAL_DELIVERY_TABS.includes(activeTable) && materialPanel === 'po-quantities')
            ) ? (
              <div className="dt-search">
                <Search className="dt-search__icon h-4 w-4" aria-hidden />
                <input
                  type="text"
                  placeholder={
                    activeTable === 'poles' ? 'Search hamlets…' :
                    activeTable === 'cables' ? 'Search cables by PO, supplier, lot…' :
                    activeTable === 'transformers' ? 'Search transformers by PO, supplier, lot…' :
                    activeTable === 'concrete-poles' ? 'Search concrete poles by PO, supplier, lot…' :
                    'Search meters by PO, supplier, lot…'
                  }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="dt-search__input"
                  aria-label="Search delivery data"
                />
              </div>
            ) : (
              <div className="dt-toolbar__spacer" aria-hidden="true" />
            )}
            <div className="dt-toolbar__spacer" aria-hidden="true" />
            <div className="dt-filters">
              {activeTable === 'poles' && polesPanel === 'delivery' && (
                <label className="dt-toggle" title="Show only rows where Delivered Quantities are filled">
                  <input
                    type="checkbox"
                    checked={onlyDeliveredFilled}
                    onChange={(e) => setOnlyDeliveredFilled(e.target.checked)}
                  />
                  Only filled Delivered Qty
                </label>
              )}
              {activeTable === 'poles' && polesPanel === 'delivery' && (
                <label
                  className="dt-toggle"
                  title="Hide Lot, Region, District, Constituency, and Ward to free space. Village and Hamlet stay visible."
                >
                  <input
                    type="checkbox"
                    checked={hideLeadingPolesColumns}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setHideLeadingPolesColumns(next);
                      try {
                        window.localStorage.setItem(POLES_HIDE_LEADING_STORAGE_KEY, next ? '1' : '0');
                      } catch {
                        /* ignore */
                      }
                    }}
                  />
                  Hide Lot–Ward columns
                </label>
              )}
              {activeTable === 'poles' && polesPanel === 'delivery' && (
                <select
                  value={supplierFilter}
                  onChange={(e) => setSupplierFilter(e.target.value)}
                  className="dt-filter-select"
                  aria-label="Supplier filter"
                  title="Filter rows by supplier"
                >
                  <option value="all">All suppliers</option>
                  {polesSupplierFilterOptions.map((supplier) => (
                    <option key={supplier} value={supplier}>{supplier}</option>
                  ))}
                </select>
              )}
              <button
                type="button"
                className="export-excel-button dt-export-btn"
                onClick={() => {
                  if (activeTable === 'cables') {
                    handleExportCableDeliveriesExcel();
                  } else if (activeTable === 'concrete-poles') {
                    handleExportConcretePolesDeliveriesExcel();
                  } else if (activeTable === 'transformers') {
                    handleExportTransformersDeliveriesExcel();
                  } else if (activeTable === 'poles') {
                    handleExportPolesDeliveriesExcel();
                  } else {
                    window.alert('Excel export for this tab is not available yet. Open the Poles, Concrete Poles, Cables, or Transformers tab to export.');
                  }
                }}
              >
                <Download className="export-icon h-4 w-4" />
                Export Excel
              </button>
            </div>
          </div>

          {activeTable === 'poles' && supplierDeliveredSummary && (
            <div className="poles-supplier-summary dt-supplier-summary">
              <div className="poles-supplier-summary-grid">
                <div className="poles-supplier-summary-supplier">
                  Supplier: <strong>{supplierDeliveredSummary.supplier}</strong>
                </div>
                {supplierDeliveredSummary.entries.length > 0 ? (
                  supplierDeliveredSummary.entries.map(([type, qty]) => (
                    <div key={type} className="poles-supplier-summary-cell">
                      <span className="poles-supplier-summary-label">{type}</span>
                      <span className="poles-supplier-summary-value"><strong>{qty.toLocaleString()}</strong></span>
                    </div>
                  ))
                ) : (
                  <div className="poles-supplier-summary-cell poles-supplier-summary-cell--empty">
                    <span className="poles-supplier-summary-value">No delivered quantities recorded yet</span>
                  </div>
                )}
              </div>
            </div>
          )}

          </div>

        <div
          className={`dt-table-body dt-table-shell${!isStatsVisible || isPolesLot10Only ? ' sticky-table-section' : ''}${['cables', 'concrete-poles', 'transformers', 'meters'].includes(activeTable) ? ' dt-table-body--cables-bare content-section--cables-bare' : ''}`}
          ref={contentSectionRef}
        >
          {activeTable === 'poles' ? (
            loading ? (
            <div className="loading-message">Loading poles data...</div>
          ) : (
            <div className={`poles-lot-section ${getPolesLotThemeClass(polesLotTab)}`}>

              {polesPanel === 'po-quantities' && !isPolesLot10Only ? (
                <>
                  <div className="dt-lot-tabs dt-lot-tabs--nested">
                    <span className="poles-po-qty-lot-hint">LOT for PO Quantities:</span>
                    {(polesLotTabs.length > 0 ? polesLotTabs : ['LOT 8', 'LOT 10']).map((lot) => (
                      <button
                        key={`po-qty-lot-${lot}`}
                        type="button"
                        className={`dt-lot-tabs__btn${
                          polesLotTab === lot ? ' is-active' : ''
                        }`}
                        onClick={() => setPolesLotTab(lot)}
                      >
                        {lot}
                      </button>
                    ))}
                  </div>
                  <PolesPoQuantitiesTable
                    lotTab={effectivePolesLotTab}
                    purchaseOrders={polesPurchaseOrders}
                    polesRows={polesData.filter(
                      (row) =>
                        !isBlankRow(row) &&
                        rowMatchesPolesLotTab(row, lotColumn, effectivePolesLotTab)
                    )}
                    editableValues={editableValues}
                    getPolesRowKey={getPolesRowKey}
                    readOnly={isReadOnly}
                  />
                </>
              ) : filteredPolesData.length === 0 ? (
                <div className="no-data-message">No poles data found</div>
              ) : (
                <>
              <div className="poles-subtotals-bar">
                <div className="poles-subtotals-marquee">
                  <div className="poles-subtotals-track">
                    <div className="poles-subtotals-content">{renderPolesSubtotalsContent()}</div>
                  </div>
                </div>
              </div>
            <div className="poles-table-wrapper">
              {/*
                Cross-hair hover highlight. We render the rules in a dynamic
                <style> block instead of toggling classes per cell so the
                14,000-cell grid never re-renders on mouse move — only this
                element changes. CSS attribute selectors target the
                `data-poles-row` / `data-poles-col` attributes we stamp on
                every body <td> and column-name <th>.
              */}
              {(polesHoverRow !== null || polesHoverCol !== null) && (
                <style>{`
                  ${polesHoverRow !== null ? `
                  .poles-table tbody td[data-poles-row="${polesHoverRow}"] {
                    background-color: #D6EBFA !important;
                  }
                  .poles-table tbody td[data-poles-row="${polesHoverRow}"].delivered-data-cell {
                    background-color: #C8E6C9 !important;
                  }
                  .poles-table tbody td[data-poles-row="${polesHoverRow}"].payments-data-cell,
                  .poles-table tbody td[data-poles-row="${polesHoverRow}"].delivery-details-data-cell {
                    background-color: #DDD6FE !important;
                  }
                  ` : ''}
                  ${polesHoverCol !== null ? `
                  .poles-table tbody td[data-poles-col="${polesHoverCol}"],
                  .poles-table thead th[data-poles-col="${polesHoverCol}"] {
                    background-color: #D6EBFA !important;
                  }
                  .poles-table thead th[data-poles-col="${polesHoverCol}"] {
                    color: #185FA5 !important;
                    box-shadow: inset 0 -2px 0 #185FA5;
                  }
                  .poles-table tbody td[data-poles-col="${polesHoverCol}"].delivered-data-cell {
                    background-color: #C8E6C9 !important;
                  }
                  .poles-table tbody td[data-poles-col="${polesHoverCol}"].payments-data-cell,
                  .poles-table tbody td[data-poles-col="${polesHoverCol}"].delivery-details-data-cell {
                    background-color: #DDD6FE !important;
                  }
                  ` : ''}
                  ${polesHoverRow !== null && polesHoverCol !== null ? `
                  .poles-table tbody td[data-poles-row="${polesHoverRow}"][data-poles-col="${polesHoverCol}"] {
                    background-color: #93C5FD !important;
                    box-shadow: inset 0 0 0 2px #185FA5 !important;
                    z-index: 2;
                    position: relative;
                  }
                  .poles-table tbody td[data-poles-row="${polesHoverRow}"][data-poles-col="${polesHoverCol}"].delivered-data-cell {
                    background-color: #66BB6A !important;
                    box-shadow: inset 0 0 0 2px #2E7D32 !important;
                  }
                  .poles-table tbody td[data-poles-row="${polesHoverRow}"][data-poles-col="${polesHoverCol}"].payments-data-cell,
                  .poles-table tbody td[data-poles-row="${polesHoverRow}"][data-poles-col="${polesHoverCol}"].delivery-details-data-cell {
                    background-color: #A78BFA !important;
                    box-shadow: inset 0 0 0 2px #534AB7 !important;
                  }
                  ` : ''}
                `}</style>
              )}
              <table className={`poles-table${hideLeadingPolesColumns ? ' poles-table--compact-identity' : ''}`}>
                <thead>
                  <tr className="boq-header-row">
                    {(() => {
                      const cells = [];
                      let processedIndices = new Set();
                      const polesHiddenLeading = hideLeadingPolesColumns
                        ? POLES_HIDEABLE_LEADING_COL_COUNT
                        : 0;
                      
                      headersWithDuplicates.forEach((header, index) => {
                        if (processedIndices.has(index)) return; // Already processed as part of colSpan
                        if (index < polesHiddenLeading) return;
                        
                        if (allBOQColumnIndices.includes(index)) {
                          // First delivered column — spanning header for Delivered Quantities
                          if (index === firstDeliveredIndex) {
                            cells.push(
                              <th
                                key={`delivered-${index}`}
                                colSpan={duplicateColumns.length}
                                className="delivered-header-cell"
                              >
                                Delivered Quantities
                              </th>
                            );
                            duplicateColumnIndices.forEach(idx => processedIndices.add(idx));
                          }
                        } else if (newColumnIndices.includes(index)) {
                          // First new column - create spanning header for Delivery Details
                          const isFirstNewColumn = newColumnIndices[0] === index;
                          if (isFirstNewColumn) {
                            cells.push(
                              <th
                                key={`delivery-details-${index}`}
                                colSpan={newColumns.length}
                                className="delivery-details-header-cell"
                              >
                                Delivery Details
                              </th>
                            );
                            // Mark new columns as processed
                            newColumnIndices.forEach(idx => processedIndices.add(idx));
                          }
                        } else if (additionalColumnIndices.includes(index)) {
                          // First additional column - create spanning header for Payments tracking
                          const isFirstAdditional = additionalColumnIndices[0] === index;
                          if (isFirstAdditional) {
                            cells.push(
                              <th
                                key={`payments-tracking-${index}`}
                                colSpan={additionalColumns.length}
                                className="payments-tracking-header-cell"
                              >
                                Payments tracking
                              </th>
                            );
                            // Mark additional columns as processed
                            additionalColumnIndices.forEach(idx => processedIndices.add(idx));
                          }
                        } else if (index === commentColumnIndex) {
                          // Comment — empty group header cell
                          cells.push(
                            <th key={`meta-header-${index}`} className="empty-header-cell"></th>
                          );
                        } else {
                          // Regular column (Hamlet info columns at the start of the row) — empty header cell
                          cells.push(
                            <th
                              key={`empty-${index}`}
                              className="empty-header-cell"
                              style={
                                polesLockedCellStyle(index, {
                                  isHeader: true,
                                  hiddenLeading: polesHiddenLeading
                                }) || undefined
                              }
                            ></th>
                          );
                        }
                      });
                      
                      return cells;
                    })()}
                  </tr>
                  <tr>
                    {headersWithDuplicates.map((header, index) => {
                      const polesHiddenLeading = hideLeadingPolesColumns
                        ? POLES_HIDEABLE_LEADING_COL_COUNT
                        : 0;
                      if (index < polesHiddenLeading) return null;
                      const trackingColWidth = getPolesTrackingColumnWidth(header);
                      const isWideColumn = trackingColWidth != null;
                      // BOQ + Delivered column-name cells live inside the
                      // 80px-wide frozen block, so "9M MEDIUM" / "10M STOUT"
                      // need a tighter font + smaller padding to fit on one
                      // line and not break to "MEDIU" / "M" on two rows.
                      const isBoqHeader = duplicateColumnIndices.includes(index);
                      const isHamletHeaderCol = isPolesHamletHeader(header);
                      const lockedHeaderStyle =
                        polesLockedCellStyle(index, {
                          isHeader: true,
                          hiddenLeading: polesHiddenLeading
                        }) || {};
                      const isLockedHeader = index < POLES_LOCKED_COLUMN_COUNT;
                      return (
                        <th
                          key={index}
                          data-poles-col={index}
                          className={isLockedHeader ? 'poles-locked-cell' : undefined}
                          style={{
                            ...(index < POLES_HAMLET_INFO_COL_COUNT ? (
                              isHamletHeaderCol ? {
                                width: `${POLES_HAMLET_COLUMN_WIDTH}px`,
                                minWidth: `${POLES_HAMLET_COLUMN_WIDTH}px`,
                                maxWidth: `${POLES_HAMLET_COLUMN_WIDTH}px`,
                                padding: '0.45rem 0.35rem',
                                fontSize: '0.65rem',
                                lineHeight: '1.2',
                                whiteSpace: 'normal',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                textAlign: 'center'
                              } : {
                                width: '80px',
                                minWidth: '80px',
                                maxWidth: '80px',
                                padding: '0.45rem 0.15rem',
                                fontSize: '0.58rem',
                                letterSpacing: '-0.01em',
                                lineHeight: '1.15',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                textAlign: 'center'
                              }
                            ) : isBoqHeader ? {
                              width: `${POLES_DELIVERED_COLUMN_WIDTH}px`,
                              minWidth: `${POLES_DELIVERED_COLUMN_WIDTH}px`,
                              maxWidth: `${POLES_DELIVERED_COLUMN_WIDTH}px`,
                              padding: '0.45rem 0.15rem',
                              fontSize: '0.58rem',
                              letterSpacing: '-0.01em',
                              lineHeight: '1.15',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              textAlign: 'center'
                            } : isWideColumn ? {
                              width: `${trackingColWidth}px`,
                              minWidth: `${trackingColWidth}px`,
                              maxWidth: `${trackingColWidth}px`,
                              fontSize: '0.65rem',
                              padding: '0.45rem 0.2rem',
                              whiteSpace: 'normal',
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                              textAlign: 'center'
                            } : {
                              fontSize: '0.7rem',
                              whiteSpace: 'normal',
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                              textAlign: 'center'
                            }),
                            // Sticky/locked styles last so left + sticky win for the frozen block.
                            ...lockedHeaderStyle,
                          }}
                        >
                          {header}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody
                  onMouseLeave={() => {
                    setPolesHoverRow(null);
                    setPolesHoverCol(null);
                  }}
                >
                  {filteredPolesData.map((row, rowIndex) => {
                    // Stable identifier for this row across reloads / filtering.
                    // All edit lookups + writes use this key (not the loop index),
                    // so persisted Survey/Delivered/Delivery-Details values
                    // stay attached to the right Hamlet row.
                    const rowKey = getPolesRowKey(row);
                    // Colour ribbon on the row's leftmost cell — matches the
                    // LOT accent palette used by the Cables/Transformers/
                    // Meters PO cards so the same LOT reads visually the same
                    // across the entire Delivery Tracking page.
                    const rowLotValue = lotColumn ? row[lotColumn] : '';
                    const lotAccentColor = getPolesLotAccentColor(rowLotValue);
                    return (
                    <tr key={rowKey || rowIndex}>
                      {headersWithDuplicates.map((header, colIndex) => {
                        const polesHiddenLeading = hideLeadingPolesColumns
                          ? POLES_HIDEABLE_LEADING_COL_COUNT
                          : 0;
                        if (colIndex < polesHiddenLeading) return null;
                        // Check if this is one of the new columns or additional columns
                        const isNewColumn = newColumns.includes(header);
                        const isAdditionalColumn = additionalColumns.includes(header);
                        const isCommentColumn = header === commentColumn;
                        // For duplicate columns, use the original column data
                        const originalHeader = header.replace(' (D)', '');
                        const isDeliveredColumn = duplicateColumnIndices.includes(colIndex);
                        const isPaymentsColumn = isAdditionalColumn;
                        
                        // Check if this is one of the last 8 columns (editable)
                        const isEditable = last8ColumnIndices.includes(colIndex);
                        
                        // Determine cell value
                        let cellValue;
                        if (isDeliveredColumn) {
                          // Delivered cells start blank — only show a value when
                          // the user has saved one.
                          cellValue = '';
                        } else if (isNewColumn || isAdditionalColumn || isCommentColumn) {
                          // Delivery Details / Payments / Comment cells start blank.
                          cellValue = '';
                        } else {
                          cellValue = row[originalHeader] || '-';
                        }
                        
                        const trackingColWidth = getPolesTrackingColumnWidth(header);
                        const isWideColumn = trackingColWidth != null;
                        const isHamletCol = isPolesHamletHeader(header);
                        const isLotCol = isPolesLotHeader(header, lotColumn);
                        const lockedBodyStyle =
                          polesLockedCellStyle(colIndex, {
                            hiddenLeading: polesHiddenLeading
                          }) || {};
                        const isPolesLocked = colIndex < POLES_LOCKED_COLUMN_COUNT;
                        const hamletDisplay =
                          isHamletCol && cellValue && cellValue !== '-'
                            ? String(cellValue)
                            : '';
                        return (
                          <td 
                            key={colIndex}
                            data-poles-row={rowIndex}
                            data-poles-col={colIndex}
                            title={hamletDisplay || undefined}
                            onMouseEnter={() => {
                              setPolesHoverRow(rowIndex);
                              setPolesHoverCol(colIndex);
                            }}
                            className={[
                              isDeliveredColumn ? 'delivered-data-cell' : '',
                              isNewColumn ? 'delivery-details-data-cell' : '',
                              isPaymentsColumn ? 'payments-data-cell' : '',
                              isCommentColumn ? 'comment-data-cell' : '',
                              isPolesLocked ? 'poles-locked-cell' : '',
                              isHamletCol ? 'poles-hamlet-cell' : '',
                              isLotCol ? 'poles-lot-cell' : '',
                            ].filter(Boolean).join(' ')}
                            style={{
                              ...(colIndex < POLES_HAMLET_INFO_COL_COUNT
                                ? isHamletCol
                                  ? {
                                      width: `${POLES_HAMLET_COLUMN_WIDTH}px`,
                                      minWidth: `${POLES_HAMLET_COLUMN_WIDTH}px`,
                                      maxWidth: `${POLES_HAMLET_COLUMN_WIDTH}px`,
                                      padding: '0.5rem 0.35rem',
                                      fontSize: '0.75rem',
                                      whiteSpace: 'normal',
                                      wordWrap: 'break-word',
                                      overflowWrap: 'break-word',
                                      lineHeight: '1.25',
                                      verticalAlign: 'top',
                                      textAlign: 'left'
                                    }
                                  : {
                                      width: '80px',
                                      minWidth: '80px',
                                      maxWidth: '80px',
                                      padding: '0.5rem 0.25rem',
                                      fontSize: '0.75rem',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }
                                : isWideColumn
                                  ? {
                                      width: `${trackingColWidth}px`,
                                      minWidth: `${trackingColWidth}px`,
                                      maxWidth: `${trackingColWidth}px`,
                                      padding: '0.35rem 0.2rem',
                                      fontSize: '0.7rem',
                                    }
                                  : {}),
                              // Locked-column inline styles last so the sticky/left/width values win for the frozen block.
                              ...lockedBodyStyle
                            }}
                          >
                            {isEditable ? (
                              header === commentColumn ? (
                                <textarea
                                  className="poles-cell-input poles-cell-textarea"
                                  rows={2}
                                  readOnly={isReadOnly}
                                  disabled={isReadOnly}
                                  value={getEditableValue(rowKey, header, cellValue !== '-' ? cellValue : '')}
                                  onChange={(e) => handleEditableChange(rowKey, header, e.target.value)}
                                  onFocus={() => {
                                    setFocusedInput(`${rowKey}-${header}`);
                                  }}
                                  onBlur={() => {
                                    setFocusedInput(null);
                                  }}
                                  placeholder="Add comment…"
                                />
                              ) : header === 'DATE DELIVERED' ? (
                                (() => {
                                  const storedDate = getPolesStoredDate(rowKey, header, '');
                                  const dateInputValue = convertDateToInputFormat(storedDate);
                                  const dateLabel = formatDateForPolesDisplay(storedDate);
                                  return (
                                    <div className="poles-date-cell">
                                      <input
                                        type="date"
                                        className="poles-date-cell-picker"
                                        readOnly={isReadOnly}
                                        disabled={isReadOnly}
                                        value={dateInputValue}
                                        onChange={(e) => {
                                          handleEditableChange(
                                            rowKey,
                                            header,
                                            e.target.value || ''
                                          );
                                        }}
                                        title="Click to pick a delivery date"
                                        aria-label={`Date delivered for row ${rowIndex + 1}`}
                                      />
                                      <span className="poles-date-cell-label">
                                        {dateLabel || 'Set date'}
                                      </span>
                                    </div>
                                  );
                                })()
                              ) : header === 'SUPPLIER' ? (
                                <select
                                  className="poles-cell-input poles-cell-select"
                                  disabled={isReadOnly}
                                  value={getEditableValue(rowKey, header, cellValue !== '-' ? cellValue : '')}
                                  onChange={(e) => handleEditableChange(rowKey, header, e.target.value)}
                                  onFocus={() => {
                                    setFocusedInput(`${rowKey}-${header}`);
                                  }}
                                  onBlur={() => {
                                    setFocusedInput(null);
                                  }}
                                >
                                  <option value="">Select Supplier</option>
                                  {polesSuppliers.map((supplier) => (
                                    <option key={supplier} value={supplier}>
                                      {supplier}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  className="poles-cell-input"
                                  readOnly={isReadOnly}
                                  disabled={isReadOnly}
                                  value={getEditableValue(rowKey, header, cellValue !== '-' ? cellValue : '')}
                                  onChange={(e) => handleEditableChange(rowKey, header, e.target.value)}
                                  onFocus={() => {
                                    setFocusedInput(`${rowKey}-${header}`);
                                  }}
                                  onBlur={() => {
                                    setFocusedInput(null);
                                  }}
                                  placeholder="-"
                                />
                              )
                            ) : isLotCol && cellValue && cellValue !== '-' ? (
                              <span className="poles-hamlet-cell-content">
                                <span
                                  className="poles-lot-ribbon"
                                  style={{ backgroundColor: lotAccentColor }}
                                  aria-hidden="true"
                                />
                                {cellValue}
                              </span>
                            ) : isHamletCol && cellValue && cellValue !== '-' ? (
                              <span className="poles-hamlet-cell-content">
                                <span
                                  className="poles-lot-ribbon"
                                  style={{ backgroundColor: lotAccentColor }}
                                  aria-hidden="true"
                                />
                                {cellValue}
                              </span>
                            ) : (
                              cellValue
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
                </>
              )}
            </div>
            )
          ) : (
            activeTable === 'cables' ||
            activeTable === 'concrete-poles' ||
            activeTable === 'transformers' ||
            activeTable === 'meters'
          ) ? (() => {
            const tabConfig = {
              cables: {
                pos: cablePurchaseOrders,
                loading: cablesLoading,
                title: 'Cable Delivery Tracking',
                empty: 'No cable purchase orders found',
                fallbackSubtitle: 'cables'
              },
              'concrete-poles': {
                pos: concretePurchaseOrders,
                loading: concreteLoading,
                title: 'Concrete Poles Delivery Tracking',
                empty: 'No concrete poles purchase orders found',
                fallbackSubtitle: 'concrete poles'
              },
              transformers: {
                pos: transformerPurchaseOrders,
                loading: transformersLoading,
                title: 'Transformer Delivery Tracking',
                empty: 'No transformer purchase orders found',
                fallbackSubtitle: 'transformers'
              },
              meters: {
                pos: meterPurchaseOrders,
                loading: metersLoading,
                title: 'Meter Delivery Tracking',
                empty: 'No meter purchase orders found',
                fallbackSubtitle: 'meters'
              }
            };
            const cfg = tabConfig[activeTable];
            const lotScopedPos = cfg.pos.filter((po) =>
              poMatchesMaterialLotTab(po, effectiveMaterialLotTab)
            );
            const visibleMaterialPos = filterMaterialDeliveryPos(lotScopedPos, searchQuery);
            const poQtyTitle = {
              cables: 'Cables PO Quantities',
              'concrete-poles': 'Concrete Poles PO Quantities',
              transformers: 'Transformers PO Quantities',
              meters: 'Meters PO Quantities'
            }[activeTable];

            if (materialPanel === 'po-quantities' && !isPolesLot10Only) {
              return (
                <>
                  <div className="dt-lot-tabs dt-lot-tabs--nested">
                    <span className="poles-po-qty-lot-hint">LOT for PO Quantities:</span>
                    {visibleMaterialLotTabs.map((lot) => (
                      <button
                        key={`material-po-qty-lot-${lot}`}
                        type="button"
                        className={`dt-lot-tabs__btn${
                          materialLotTab === lot ? ' is-active' : ''
                        }`}
                        onClick={() => setMaterialLotTab(lot)}
                      >
                        {lot}
                      </button>
                    ))}
                  </div>
                  <MaterialPoQuantitiesTable
                    title={poQtyTitle}
                    lotTab={effectiveMaterialLotTab}
                    purchaseOrders={cfg.pos}
                    aggregateByCableType={activeTable === 'cables'}
                    cableTablesState={cableTablesState}
                    computeRowDelivered={calculateRowTotal}
                    normalizePoItemsForTable={normalizePoItemsForTable}
                    getPoQtyNumeric={getPoQtyNumeric}
                    contractStorageScope={`${activeTable}:${effectiveMaterialLotTab}`}
                    readOnly={isReadOnly}
                  />
                </>
              );
            }

            return (
              cfg.loading ? (
                <div className="cables-feedback-message">Loading purchase orders…</div>
              ) : cfg.pos.length === 0 ? (
                <div className="cables-feedback-message">{cfg.empty}</div>
              ) : lotScopedPos.length === 0 ? (
                <div className="cables-feedback-message">
                  No purchase orders for {effectiveMaterialLotTab}.
                </div>
              ) : visibleMaterialPos.length === 0 ? (
                <div className="cables-feedback-message">
                  No purchase orders match &ldquo;{searchQuery.trim()}&rdquo;.
                </div>
              ) : (
              <>
                {visibleMaterialPos.map((po) => {
                  const rowData = normalizePoItemsForTable(po);
                  const tableState = cableTablesState[po.id] || {
                    deliveryDates: [...EMPTY_CABLE_DELIVERY_SLOTS],
                    deliveryNumbers: [...EMPTY_CABLE_DELIVERY_SLOTS],
                    deliveryData: buildInitialDeliveryDataForItems(rowData)
                  };
                  const deliveryColIndexes = getCableDeliveryColIndexes();
                  const deliveryNumbersForPo = padCableDeliveryArray(tableState.deliveryNumbers, EMPTY_CABLE_DELIVERY_SLOTS);
                  const deliveryDatesForPo = padCableDeliveryArray(tableState.deliveryDates, EMPTY_CABLE_DELIVERY_SLOTS);
                  const isMaterialDeliveryTab = MATERIAL_DELIVERY_TABS.includes(activeTable);
                  const isMaterialPoExpanded =
                    !isMaterialDeliveryTab || expandedMaterialPoIds.has(po.id);
                  const materialPoSummary = isMaterialDeliveryTab
                    ? getMaterialPoSummary(po, rowData, tableState)
                    : null;
                  return (
                    <div
                      key={po.id}
                      className={`po-table-container ${lotAccentClass(po.lot)}${
                        activeTable === 'concrete-poles' || activeTable === 'transformers'
                          ? ' po-table-container--concrete-meta'
                          : ''
                      }${isMaterialDeliveryTab ? ' po-table-container--material-collapsible' : ''}${
                        isMaterialDeliveryTab && isMaterialPoExpanded ? ' is-expanded' : ''
                      }`}
                    >
                      <div className="cables-container">
                      <div className="cables-table-title-bar">
                        <span className="cables-table-title">{cfg.title}</span>
                        <div className="cables-table-title-bar__actions">
                          <span className="cables-table-subtitle">{po.subcategory || cfg.fallbackSubtitle}</span>
                          {isMaterialDeliveryTab && (
                            <button
                              type="button"
                              className="cables-expand-toggle"
                              onClick={() => toggleMaterialPoExpanded(po.id)}
                              aria-expanded={isMaterialPoExpanded}
                              aria-controls={`material-delivery-table-${po.id}`}
                            >
                              {isMaterialPoExpanded ? 'Collapse' : 'Expand to edit'}
                              <ChevronDown
                                className={`cables-expand-toggle__chevron${
                                  isMaterialPoExpanded ? ' is-open' : ''
                                }`}
                                aria-hidden
                              />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="cables-header-section-fixed compact-header">
                        <div className="cables-header-left compact-meta">
                          <div className="cables-meta-item">
                            <span className="cables-label">Supplier:</span>
                            <span className="cables-supplier-value">{po.supplierName || '-'}</span>
                          </div>
                          <div className="cables-meta-item">
                            <span className="cables-order-label">Order No:</span>
                            <span className="cables-order-numbers">{po.poNumber || '-'}</span>
                          </div>
                          <div className="cables-meta-item">
                            <span className="cables-lot-label">Lot name:</span>
                            <span className="cables-lot-value">{po.lot || '-'}</span>
                          </div>
                        </div>
                        {isMaterialDeliveryTab && !isMaterialPoExpanded && materialPoSummary && (
                          <div className="cables-po-summary" aria-live="polite">
                            <span className="cables-po-summary__stat">
                              <strong>{materialPoSummary.itemCount}</strong> item
                              {materialPoSummary.itemCount === 1 ? '' : 's'}
                            </span>
                            <span className="cables-po-summary__stat">
                              <strong>{materialPoSummary.pctDelivered}%</strong> delivered
                            </span>
                            <span className="cables-po-summary__stat">
                              <strong>{materialPoSummary.filledDns}</strong> delivery note
                              {materialPoSummary.filledDns === 1 ? '' : 's'}
                            </span>
                          </div>
                        )}
                      </div>
                      {isMaterialPoExpanded && (
                      <div className="cables-table-wrapper" id={`material-delivery-table-${po.id}`}>
                        <table className="cables-table">
                          <colgroup>
                            <col style={{ width: '44px' }} />
                            <col className="cables-col-items" style={{ width: '9.5rem' }} />
                            <col style={{ width: '112px' }} />
                            {Array.from({ length: CABLE_DELIVERY_COLUMN_COUNT }, (_, i) => (
                              <col key={`dn-col-${i}`} style={{ width: '100px' }} />
                            ))}
                            <col style={{ width: '108px' }} />
                            <col style={{ width: '82px' }} />
                            <col style={{ width: '108px' }} />
                          </colgroup>
                          <thead>
                            <tr className="cables-sub-header-row">
                              <th colSpan="3"></th>
                              <th colSpan={CABLE_DELIVERY_COLUMN_COUNT} className="cables-deliveries-header">
                                <div className="cables-deliveries-title">Deliveries</div>
                              </th>
                              <th colSpan="3"></th>
                            </tr>
                            <tr className="cables-deliveries-numbers-row">
                              <th colSpan="3"></th>
                              {deliveryNumbersForPo.map((number, index) => {
                                const deliveryColIndex = CABLE_DELIVERY_COL_START + index;
                                const hasQty = hasQtyInDeliveryColumn(tableState.deliveryData, deliveryColIndex);
                                const displayNumber =
                                  number !== undefined && number !== null && String(number).trim() !== ''
                                    ? String(number).trim()
                                    : '';
                                return (
                                <th key={index} className="cables-delivery-number">
                                  {activeDeliveryNumberEditor?.poId === po.id && activeDeliveryNumberEditor?.index === index ? (
                                    <div className="delivery-number-edit-container">
                                      <div className="delivery-number-edit-label" id={`dn-edit-label-${po.id}-${index}`}>
                                        Editing DN · Delivery {index + 1}
                                      </div>
                                      <input
                                        type="text"
                                        value={deliveryNumberEditorValue}
                                        onChange={(e) => setDeliveryNumberEditorValue(e.target.value)}
                                        className="delivery-number-edit-input"
                                        autoFocus
                                        aria-labelledby={`dn-edit-label-${po.id}-${index}`}
                                        placeholder="e.g. DN-1, 8940"
                                      />
                                      <div className="delivery-number-edit-actions">
                                        <button type="button" className="delivery-number-save-btn" onClick={saveDeliveryNumberEditor}>Save</button>
                                        <button type="button" className="delivery-number-cancel-btn" onClick={closeDeliveryNumberEditor}>Cancel</button>
                                      </div>
                                    </div>
                                  ) : (
                                    <button
                                      type="button"
                                      className="delivery-number-display-button"
                                      onClick={() => openDeliveryNumberEditor(po.id, index, displayNumber)}
                                      title={
                                        hasQty
                                          ? `Edit delivery note (DN) for delivery column ${index + 1}`
                                          : `Edit delivery note (DN) for delivery column ${index + 1}. Enter quantities below to enable the date for this column.`
                                      }
                                    >
                                      {displayNumber || '—'}
                                    </button>
                                  )}
                                </th>
                              )})}
                              <th colSpan="3"></th>
                            </tr>
                            <tr className="cables-main-header">
                              <th>S/No:</th>
                              <th className="cables-items-header">Items</th>
                              <th className="cables-po-header">PO Qty's (M)</th>
                              {(deliveryDatesForPo).map((date, index) => {
                                const deliveryColIndex = CABLE_DELIVERY_COL_START + index;
                                const hasQty = hasQtyInDeliveryColumn(tableState.deliveryData, deliveryColIndex);
                                // Columns with no delivered qty keep the "Set date" placeholder.
                                const dateToShow = hasQty ? date : '';
                                return (
                                <th key={index} className="cables-delivery-date">
                                  <input
                                    type="date"
                                    className="cables-delivery-date-input"
                                    value={deliveryDateToInputValue(dateToShow)}
                                    onChange={(e) => {
                                      const formatted = inputValueToDeliveryDate(e.target.value);
                                      if (formatted) {
                                        handleCablesDateChange(po.id, index, formatted);
                                      }
                                    }}
                                    title={
                                      hasQty
                                        ? 'Click to set delivery date for this column'
                                        : 'Enter quantities in this delivery column first, then set the date'
                                    }
                                    aria-label={`Delivery date ${index + 1}`}
                                  />
                                  <span className="cables-delivery-date-label" aria-hidden="true">
                                    {formatDateForDisplay(dateToShow) || 'Set date'}
                                  </span>
                                </th>
                                );
                              })}
                              <th className="cables-total-delivery">Total Delivery</th>
                              <th className="cables-percent-delivered">% Delivered</th>
                              <th className="cables-po-balance-header">PO Qty balance (M)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rowData.map((item, rowIndex) => {
                              const poQty = getDisplayPoQty(po, item?.qty, item?.itemName);
                              const lastDeliveryCol = deliveryColIndexes[deliveryColIndexes.length - 1];
                              return (
                                <tr key={`${po.id}-${rowIndex}`}>
                                  <td>{rowIndex + 1}</td>
                                  <td className="cables-item-cell">{item?.itemName || '-'}</td>
                                  <td className="cables-po-cell">{poQty || '-'}</td>
                                  {deliveryColIndexes.map((colIndex) => {
                                    const cellValue = tableState.deliveryData?.[rowIndex]?.[colIndex];
                                    const displayValue = cellValue === undefined || cellValue === null || cellValue === '' ? '' : cellValue;
                                    const isActiveEditor =
                                      activeDeliveryEditor?.poId === po.id &&
                                      activeDeliveryEditor?.rowIndex === rowIndex &&
                                      activeDeliveryEditor?.colIndex === colIndex;
                                    const deliveryColumnNo = colIndex - 2;
                                    const itemLabel = String(item?.itemName || 'Item').trim() || 'Item';
                                    const cellEditLabelId = `cable-delivery-edit-${po.id}-${rowIndex}-${colIndex}`;
                                    return (
                                      <td
                                        key={colIndex}
                                        className={`cables-delivery-cell cables-delivery-cell-editor${
                                          colIndex === lastDeliveryCol ? ' cables-delivery-cell--last' : ''
                                        }`}
                                      >
                                        {isActiveEditor ? (
                                          <div className="cell-edit-container cables-delivery-cell-edit">
                                            <div className="cell-edit-label" id={cellEditLabelId}>
                                              Editing delivery · Row {rowIndex + 1} · Delivery {deliveryColumnNo}
                                              <span className="cell-edit-item-name">{itemLabel}</span>
                                            </div>
                                            <label className="cables-delivery-date-edit-label">
                                              Delivery date
                                              <input
                                                type="date"
                                                className="cables-delivery-date-edit-input"
                                                value={deliveryEditorDateValue}
                                                onChange={(e) => setDeliveryEditorDateValue(e.target.value)}
                                                aria-label={`Delivery date for column ${deliveryColumnNo}`}
                                              />
                                            </label>
                                            <input
                                              type="text"
                                              value={deliveryEditorValue}
                                              onChange={(e) => setDeliveryEditorValue(e.target.value)}
                                              placeholder="Qty (m)"
                                              className="cell-edit-input"
                                              autoFocus
                                              aria-labelledby={cellEditLabelId}
                                            />
                                            <div className="cell-edit-actions">
                                              <button type="button" className="cell-save-btn" onClick={saveDeliveryCellEditor}>Save</button>
                                              <button type="button" className="cell-cancel-btn" onClick={closeDeliveryCellEditor}>Cancel</button>
                                            </div>
                                          </div>
                                        ) : (
                                          <button
                                            type="button"
                                            className="cell-display-button"
                                            title={`Edit delivered qty · ${itemLabel} · Delivery column ${deliveryColumnNo}`}
                                            onClick={() =>
                                              openDeliveryCellEditor(
                                                po.id,
                                                rowIndex,
                                                colIndex,
                                                displayValue,
                                                tableState.deliveryDates
                                              )
                                            }
                                          >
                                            {displayValue || '-'}
                                          </button>
                                        )}
                                      </td>
                                    );
                                  })}
                                  <td className="cables-total-cell">{formatTotal(calculateRowTotal(rowIndex, tableState.deliveryData))}</td>
                                  <td className="cables-percent-cell">{calculatePercentDelivered(rowIndex, poQty, tableState.deliveryData)}</td>
                                  <td className="cables-po-balance-cell">
                                    {getPoQtyBalanceDisplay(po, item, rowIndex, tableState.deliveryData)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      )}
                      </div>
                    </div>
                  );
                })}
              </>
            ));
          })() : (
            <div className="poles-table-wrapper">
              <div className="no-data-message" style={{ padding: '3rem', textAlign: 'center' }}>
              </div>
            </div>
          )}
        </div>
      </div>
        </div>
      </div>
    </div>
  );
}

export default DeliveryTracking;

